import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Badge, Form, Modal, ListGroup, Row, Col, Table, Tabs, Tab, Alert } from 'react-bootstrap';
import axios from 'axios';
import { PurchaseRequest, PurchaseComment, PurchaseAttachment, User, Group, Workflow, PurchaseActivityLog, PurchaseTime, WorkflowCustomField, PurchaseCustomFieldValue, PurchaseApproval, PurchaseFinalReport, DocumentInstance } from '../types';
import { API_URL } from '../config';
import { downloadFile } from '../utils/fileDownload';
import { getSingleCustomFieldFileDisplayName, parseCustomFieldFilePathsJson } from '../utils/customFieldFileDisplay';
import { formatApiErrorDetail } from '../utils/apiError';
import { userCanManageOpportunities } from '../constants/opportunityAccess';
import { useAuth } from '../context/AuthContext';
import { useArea } from '../context/AreaContext';
import { useModal } from '../context/ModalContext';
import WorkflowVisualizerHorizontal from '../components/WorkflowVisualizerHorizontal';
import AlertModal from '../components/AlertModal';
import CustomFieldsEditor from '../components/CustomFieldsEditor';
import PurchaseSubtaskModal, { PurchaseSubtaskSubmitPayload } from '../components/PurchaseSubtaskModal';
import { ATTACHMENT_ACCEPT, validateAttachmentFile } from '../utils/uploadValidation';
import { URGENCY_OPTIONS, getUrgencyColor, getUrgencyLabel, type TicketUrgency } from '../utils/urgency';
import { PRIORITY_OPTIONS, getPriorityColor, getPriorityLabel } from '../utils/priority';
import { canManagePurchasePriority } from '../utils/roles';
import { suppliersApi } from '../services/suppliers';
import { applyColorClassToSelection, linkifyRichTextHtml, sanitizeRichTextHtml } from '../utils/richTextSanitizer';
import { isSupplierSelectionFieldType } from '../utils/supplierSelection';
import { isCustomFieldOriginAttachment } from '../utils/purchaseAttachmentOrigin';
import { elapsedSecondsSince, resolvePurchaseDisplayTimes } from '../utils/purchaseElapsedTime';
import ApproverQuestionsForm, {
  ApproverAnswersState,
  ApproverQuestionAnswers,
} from '../components/ApproverQuestionsForm';
import {
  buildApproverAnswersPayload,
  parseApproverAnswerRecords,
  parseApproverQuestions,
  validateApproverAnswers,
} from '../utils/approverQuestions';

/** Intervalo de ressincronização dos tempos com o backend enquanto a tela fica aberta. */
const PURCHASE_TIMES_RESYNC_MS = 60_000;

const sanitizeHTML = (html: string): string => {
  const isAllowedHref = (href: string): boolean => {
    if (!href) return false;
    const normalized = href.trim();
    if (normalized.startsWith('/')) return true;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) return true;
    if (normalized.startsWith('mailto:')) return true;
    return normalized.startsWith(API_URL);
  };
  return sanitizeRichTextHtml(html, { isAllowedHref });
};

const enrichCommentLinks = (html: string): string => {
  const isAllowedHref = (href: string): boolean => {
    if (!href) return false;
    const normalized = href.trim();
    if (normalized.startsWith('/')) return true;
    if (normalized.startsWith('http://') || normalized.startsWith('https://')) return true;
    if (normalized.startsWith('mailto:')) return true;
    return normalized.startsWith(API_URL);
  };
  return linkifyRichTextHtml(html, { isAllowedHref });
};

// Funções auxiliares
const isWaitingStatus = (status: string): boolean => {
  return status.startsWith('aguardando_');
};

const isFinalStatus = (status: string): boolean => {
  return status === 'fechado' || status === 'cancelado';
};

const stripBidiControlChars = (value: string): string =>
  value.replace(/[\u202A-\u202E\u2066-\u2069]/g, '');

const normalizePurchaseStatus = (status?: string | null): string => {
  if (!status) return '';
  const raw = String(status).trim().toLowerCase();
  return raw.includes('.') ? (raw.split('.').pop() ?? raw) : raw;
};

const isPurchaseTimerPausedStatus = (status?: string | null): boolean => {
  const token = normalizePurchaseStatus(status);
  return token === 'aguardando_aprovacao' || token === 'aguardando_reenvio';
};

const normalizeStepName = (value?: string | null): string =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const isOpportunityPreparationStep = (step?: string | null): boolean => {
  const normalized = normalizeStepName(step);
  return normalized.includes('preparacao') && normalized.includes('oportunidade');
};

const getStatusColor = (status: string) => {
  const colors: { [key: string]: string } = {
    novo: 'primary',
    aberto: 'warning',
    em_andamento: 'info',
    aguardando_informacao_usuario: 'secondary',
    aguardando_terceiros: 'secondary',
    aguardando_evidencia: 'secondary',
    aguardando_problema: 'secondary',
    aguardando_implantacao: 'secondary',
    aguardando_aprovacao: 'primary',
    aguardando_reenvio: 'danger',
    resolvido: 'success',
    cancelado: 'danger',
    fechado: 'dark',
  };
  return colors[status] || 'secondary';
};

const getStatusLabel = (status: string): string => {
  const labels: { [key: string]: string } = {
    novo: 'Novo',
    aberto: 'Aberto',
    em_andamento: 'Em Andamento',
    aguardando_informacao_usuario: 'Aguardando Informação do Usuário',
    aguardando_terceiros: 'Aguardando Terceiros',
    aguardando_evidencia: 'Aguardando Evidência',
    aguardando_problema: 'Aguardando Problema',
    aguardando_implantacao: 'Aguardando Implantação',
    aguardando_aprovacao: 'Aguardando Aprovação',
    aguardando_reenvio: 'Aguardando Reenvio',
    resolvido: 'Resolvido',
    cancelado: 'Cancelado',
    fechado: 'Fechado',
  };
  return labels[status] || status;
};

const PURCHASE_CLASSIFICATION_EDIT_STATUSES = new Set([
  'novo',
  'aberto',
  'em_andamento',
  'aguardando_aprovacao',
  'aguardando_reenvio',
]);

const getActivityColor = (activityType: string) => {
  const colors: { [key: string]: string } = {
    criado: 'success',
    distribuido: 'info',
    atribuido: 'primary',
    assumido: 'warning',
    status_alterado: 'secondary',
    comentario: 'info',
    anexo: 'dark',
    encerrado: 'danger',
    aprovado: 'success',
    reprovado: 'danger',
  };
  return colors[activityType.toLowerCase()] || 'secondary';
};

const PROPOSAL_TAB_NAME = 'Propostas';
const PROPOSAL_STAGE_MARKERS = [
  'avaliacao de fornecedores',
  'solicitacao de propostas',
  'recebimento de propostas',
];
const PROPOSAL_FIELD_MARKERS = [
  'proposta',
  'propostas',
  'cotacao',
  'cotacao de fornecedores',
  'fornecedor_sugerido',
  'fornecedores_sugeridos',
  'fornecedor sugerido',
  'fornecedores sugeridos',
];
const PROPOSAL_ATTACHMENT_MARKERS = [
  'proposta',
  'propostas',
  'cotacao',
  'cotacao de fornecedores',
  'comercial',
  'orcamento',
  'fornecedor',
];
const CHECKLIST_STEP_MARKERS = [
  'avaliacao do checklist',
  'avaliacao de checklist',
  'checklist de seguranca',
];
const CHECKLIST_QUESTION_PREFIX = 'q_checklist_auto_';
const CHECKLIST_ATTACHMENT_PREFIX = 'anexo_q_checklist_auto_';
const SUBTASK_STEP_GROUP_RULES: Record<string, string[]> = {
  'validacao de compliance': ['gdca'],
  'validacao do juridico': ['jur', 'juridico'],
  'validacao de si': ['si', 'seguranca da informacao'],
};

const normalizeProposalToken = (value?: string | null): string => {
  return (value || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

const hasProposalMarker = (value: string, markers: string[]): boolean => {
  if (!value) return false;
  return markers.some((marker) => value.includes(marker));
};

const isProposalStage = (step?: string | null): boolean => {
  const normalized = normalizeProposalToken(step);
  if (!normalized) return false;
  return hasProposalMarker(normalized, PROPOSAL_STAGE_MARKERS);
};

const isChecklistStage = (step?: string | null): boolean => {
  const normalized = normalizeProposalToken(step);
  if (!normalized) return false;
  return hasProposalMarker(normalized, CHECKLIST_STEP_MARKERS);
};

const isChecklistRelatedField = (field?: WorkflowCustomField | null): boolean => {
  if (!field) return false;
  const normalizedName = normalizeProposalToken(field.name);
  return (
    normalizedName.startsWith(CHECKLIST_QUESTION_PREFIX) ||
    normalizedName.startsWith(CHECKLIST_ATTACHMENT_PREFIX)
  );
};

const isProposalRelatedField = (field?: WorkflowCustomField | null): boolean => {
  if (!field) return false;
  if (isSupplierSelectionFieldType(field.field_type)) return true;
  const tokens = [
    normalizeProposalToken(field.name),
    normalizeProposalToken(field.label),
    normalizeProposalToken(field.group_name),
    normalizeProposalToken(field.description),
  ];
  return tokens.some((token) => hasProposalMarker(token, PROPOSAL_FIELD_MARKERS));
};

const isProposalLikeAttachment = (filename?: string | null, description?: string | null): boolean => {
  const text = `${normalizeProposalToken(filename)} ${normalizeProposalToken(description)}`.trim();
  if (!text) return false;
  return hasProposalMarker(text, PROPOSAL_ATTACHMENT_MARKERS);
};

const allowedGroupMarkersForStep = (step?: string | null): string[] => {
  const normalizedStep = normalizeProposalToken(step);
  if (!normalizedStep) return [];
  for (const [stepMarker, groupMarkers] of Object.entries(SUBTASK_STEP_GROUP_RULES)) {
    if (normalizedStep.includes(stepMarker)) {
      return groupMarkers;
    }
  }
  return [];
};

const parseWorkflowStepNames = (rawSteps?: string | null): string[] => {
  if (!rawSteps) return [];
  try {
    const parsed = JSON.parse(rawSteps);
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry: any) => (typeof entry === 'string' ? entry : (entry?.name || entry?.step || '')))
        .map((name: string) => String(name || '').trim())
        .filter(Boolean);
    }
    if (parsed?.steps && Array.isArray(parsed.steps)) {
      return parsed.steps
        .map((entry: any) => (typeof entry === 'string' ? entry : (entry?.name || entry?.step || '')))
        .map((name: string) => String(name || '').trim())
        .filter(Boolean);
    }
  } catch {
    return rawSteps.split(',').map((entry) => entry.trim()).filter(Boolean);
  }
  return [];
};

const isInitialPurchaseStep = (step?: string | null, workflowSteps: string[] = []): boolean => {
  const normalized = normalizeProposalToken(step);
  const byMarker = (
    normalized.includes('recebimento da requisicao') ||
    normalized.includes('abertura da requisicao')
  );
  if (byMarker) return true;
  const firstWorkflowStep = workflowSteps[0];
  if (!firstWorkflowStep || !normalized) return false;
  return normalizeProposalToken(firstWorkflowStep) === normalized;
};

type LinkedDocumentItem = {
  field?: WorkflowCustomField;
  document?: DocumentInstance;
  filePath?: string;
  fileName?: string;
  fileMissing?: boolean;
};

const buildLinkedDocumentsFromCustomFieldValues = (
  customFieldValues: PurchaseCustomFieldValue[],
  customFields: WorkflowCustomField[],
): LinkedDocumentItem[] => {
  const customFieldById = new Map<number, WorkflowCustomField>(
    customFields.map((field) => [field.id, field])
  );
  const resolveField = (value: PurchaseCustomFieldValue) =>
    customFieldById.get(value.custom_field_id);

  const seen = new Set<string>();
  const linkedDocuments: LinkedDocumentItem[] = [];

  const pushUnique = (item: LinkedDocumentItem, key: string) => {
    if (seen.has(key)) return;
    seen.add(key);
    linkedDocuments.push(item);
  };

  for (const value of customFieldValues) {
    const field = resolveField(value);
    if (!field) continue;
    const fieldId = value.custom_field_id;

    if (value.document_instance_id && value.document_instance) {
      pushUnique(
        { field, document: value.document_instance },
        `doc:${fieldId}:${value.document_instance_id}`,
      );
      continue;
    }

    if (value.file_path) {
      pushUnique(
        {
          field,
          filePath: value.file_path,
          fileName: getSingleCustomFieldFileDisplayName(value.file_path, value.text_value),
          fileMissing: value.file_missing,
        },
        `file:${fieldId}:${value.file_path}`,
      );
    }

    if (value.file_paths) {
      parseCustomFieldFilePathsJson(value.file_paths).forEach((entry) => {
        if (!entry.path) return;
        pushUnique(
          {
            field,
            filePath: entry.path,
            fileName: entry.displayName,
            fileMissing: entry.fileMissing,
          },
          `files:${fieldId}:${entry.path}`,
        );
      });
    }
  }

  return linkedDocuments.filter((item) => item.document || item.filePath);
};

const extractSupplierIdsFromActivityValue = (value?: string | null): number[] => {
  const raw = String(value || '').trim();
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as Record<string, unknown>).selected_supplier_ids)) {
      return ((parsed as Record<string, unknown>).selected_supplier_ids as unknown[])
        .map((id) => Number(String(id ?? '').trim()))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
  } catch {
    // fallback para formato legado abaixo
  }

  const selectedMatch = raw.match(/['"]?selected_supplier_ids['"]?\s*:\s*\[([\s\S]*?)\]/i);
  const ids = selectedMatch?.[1]?.match(/-?\d+/g) || [];
  return ids
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
};

const formatSupplierSelectionPayload = (
  payload: Record<string, unknown>,
  supplierNamesById: Record<number, string>
): string | null => {
  if (!('selected_supplier_ids' in payload) && !('manual_suppliers' in payload)) {
    return null;
  }

  const selectedSupplierIds = Array.isArray(payload.selected_supplier_ids)
    ? payload.selected_supplier_ids
        .map((id) => Number(String(id ?? '').trim()))
        .filter((id) => Number.isFinite(id) && id > 0)
    : [];

  const manualSuppliers = Array.isArray(payload.manual_suppliers)
    ? payload.manual_suppliers.filter((entry) => entry && typeof entry === 'object') as Array<Record<string, unknown>>
    : [];

  const selectedPart = selectedSupplierIds.length > 0
    ? `Fornecedores selecionados: ${selectedSupplierIds.map((id) => {
        const name = supplierNamesById[id];
        return name ? `${name} (#${id})` : `#${id}`;
      }).join(', ')}`
    : 'Fornecedores selecionados: nenhum';

  const manualNames = manualSuppliers
    .map((supplier) =>
      String(
        supplier.company_name ||
        supplier.trade_name ||
        supplier.name ||
        supplier.razao_social ||
        ''
      ).trim()
    )
    .filter((name) => name.length > 0);

  const manualPart = manualNames.length > 0
    ? `Fornecedores manuais: ${manualNames.join(', ')}`
    : manualSuppliers.length > 0
      ? `Fornecedores manuais: ${manualSuppliers.length}`
      : 'Fornecedores manuais: nenhum';

  return `${selectedPart} | ${manualPart}`;
};

const formatSupplierSelectionFromLegacyRaw = (
  raw: string,
  supplierNamesById: Record<number, string>
): string | null => {
  const selectedMatch = raw.match(/['"]?selected_supplier_ids['"]?\s*:\s*\[([\s\S]*?)\]/i);
  const manualMatch = raw.match(/['"]?manual_suppliers['"]?\s*:\s*\[([\s\S]*?)\]/i);

  if (!selectedMatch && !manualMatch) {
    return null;
  }

  const ids = (selectedMatch?.[1]?.match(/-?\d+/g) || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id) && id > 0);
  const selectedPart = ids.length > 0
    ? `Fornecedores selecionados: ${ids.map((id) => {
        const name = supplierNamesById[id];
        return name ? `${name} (#${id})` : `#${id}`;
      }).join(', ')}`
    : 'Fornecedores selecionados: nenhum';

  const manualBlock = manualMatch?.[1] || '';
  const manualNameRegex = /['"](company_name|trade_name|name|razao_social)['"]\s*:\s*['"]([^'"]+)['"]/gi;
  const manualNames: string[] = [];
  let match: RegExpExecArray | null = null;
  while ((match = manualNameRegex.exec(manualBlock)) !== null) {
    const name = String(match[2] || '').trim();
    if (name) manualNames.push(name);
  }

  const hasManualEntries = manualBlock.trim().length > 0;
  const manualPart = manualNames.length > 0
    ? `Fornecedores manuais: ${manualNames.join(', ')}`
    : hasManualEntries
      ? 'Fornecedores manuais: 1+'
      : 'Fornecedores manuais: nenhum';

  return `${selectedPart} | ${manualPart}`;
};

const formatActivityValue = (
  value?: string | null,
  supplierNamesById: Record<number, string> = {}
): string => {
  const raw = String(value || '').trim();
  if (!raw) return 'vazio';

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      const payload = parsed as Record<string, unknown>;

      // Friendly summary for technical proposal review payloads.
      if ('decision' in payload || 'opinion' in payload) {
        const decisionMap: Record<string, string> = {
          approved: 'Aprovada',
          clarifications_for_approval: 'Aprovada com ressalvas',
          rejected: 'Reprovada',
        };
        const decision = decisionMap[String(payload.decision || '')] || String(payload.decision || 'Não informado');
        const opinion = String(payload.opinion || '').trim();
        const source = String(payload.proposal_source || '').trim();
        const sourceLabel = source === 'existing_attachment' ? 'Anexo existente' : source === 'upload' ? 'Upload' : source;
        return `${decision}${opinion ? ` | Parecer: ${opinion}` : ''}${sourceLabel ? ` | Origem: ${sourceLabel}` : ''}`;
      }

      const supplierSummary = formatSupplierSelectionPayload(payload, supplierNamesById);
      if (supplierSummary) {
        return supplierSummary;
      }

      return JSON.stringify(parsed, null, 2);
    }
  } catch {
    // keep plain text fallback
  }

  const legacySupplierSummary = formatSupplierSelectionFromLegacyRaw(raw, supplierNamesById);
  if (legacySupplierSummary) {
    return legacySupplierSummary;
  }

  return raw;
};

const PurchaseRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const handleNavigateBack = useCallback(() => {
    const from = (location.state as { from?: string } | null)?.from;
    if (from) {
      navigate(from);
      return;
    }
    navigate(-1);
  }, [navigate, location.state]);
  const { user: currentUser } = useAuth();
  const { currentArea } = useArea();
  const { showAlert: showModalAlert, showConfirm } = useModal();
  const [purchaseRequest, setPurchaseRequest] = useState<PurchaseRequest | null>(null);
  const [comments, setComments] = useState<PurchaseComment[]>([]);
  const [attachments, setAttachments] = useState<PurchaseAttachment[]>([]);
  const [subtasks, setSubtasks] = useState<PurchaseRequest[]>([]);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [canAdvanceStepFromAPI, setCanAdvanceStepFromAPI] = useState(false);
  const [canGoBackStepFromAPI, setCanGoBackStepFromAPI] = useState(false);
  const [canConclude, setCanConclude] = useState(false);
  const [blockedByPendingApprovalsApi, setBlockedByPendingApprovalsApi] = useState(false);
  const [canCancelPurchase, setCanCancelPurchase] = useState(false);
  const [cancellingPurchase, setCancellingPurchase] = useState(false);
  const [isLastStep, setIsLastStep] = useState(false);
  const [nextStepOptions, setNextStepOptions] = useState<Array<{label?: string; step: string} | string>>([]);
  const [missingRequiredFields, setMissingRequiredFields] = useState<string[]>([]);
  const [selectedNextStep, setSelectedNextStep] = useState('');
  const [isGoingBackStep, setIsGoingBackStep] = useState(false);
  const [isConcluding, setIsConcluding] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  /** Fluxo exato da requisição (versão antiga pode não aparecer em GET /workflows?only_valid). */
  const [boundWorkflow, setBoundWorkflow] = useState<Workflow | null>(null);
  const [activities, setActivities] = useState<PurchaseActivityLog[]>([]);
  const [supplierNamesById, setSupplierNamesById] = useState<Record<number, string>>({});
  const [purchaseRequestTimes, setpurchaseRequestTimes] = useState<PurchaseTime | null>(null);
  const [loading, setLoading] = useState(true);
  // Instante da última resposta de /times e relógio atual: o tempo exibido é sempre o valor do
  // servidor mais os segundos decorridos desde então, e não um contador que reinicia no refresh.
  const [timesFetchedAtMs, setTimesFetchedAtMs] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSubtaskModal, setShowSubtaskModal] = useState(false);
  const [showWaitingReasonModal, setShowWaitingReasonModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string>('');
  const [waitingReason, setWaitingReason] = useState('');
  const [commentText, setCommentText] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rejectionJustification, setRejectionJustification] = useState('');
  const [approverAnswers, setApproverAnswers] = useState<ApproverAnswersState>({});
  const [approvalInAction, setApprovalInAction] = useState<PurchaseApproval | null>(null);
  const [processingApproval, setProcessingApproval] = useState(false);
  const [resubmittingApproval, setResubmittingApproval] = useState(false);
  const [returningForRequesterReview, setReturningForRequesterReview] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ title: '', message: '', variant: 'info' as 'success' | 'danger' | 'warning' | 'info', details: '' });
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [attachmentVisibleToRequester, setAttachmentVisibleToRequester] = useState(false);
  const [updatingAttachmentVisibilityId, setUpdatingAttachmentVisibilityId] = useState<number | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [isExportingFinalReport, setIsExportingFinalReport] = useState(false);
  const [showFinalReportModal, setShowFinalReportModal] = useState(false);
  const [finalReportWithProposalCalculations, setFinalReportWithProposalCalculations] = useState(false);
  const [isLoadingFinalReportOptions, setIsLoadingFinalReportOptions] = useState(false);
  const [finalReportPreview, setFinalReportPreview] = useState<PurchaseFinalReport | null>(null);
  const [selectedFinalReportFieldIds, setSelectedFinalReportFieldIds] = useState<number[]>([]);
  const [selectedFinalReportFormat, setSelectedFinalReportFormat] = useState<'xlsx' | 'pdf'>('xlsx');
  const [customFields, setCustomFields] = useState<WorkflowCustomField[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<PurchaseCustomFieldValue[]>([]);
  const [activeProcessInfoTab, setActiveProcessInfoTab] = useState<string>('');
  const [isCreatingSubtask, setIsCreatingSubtask] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [proposalAccessDenied, setProposalAccessDenied] = useState(false);
  const [stageAccessDenied, setStageAccessDenied] = useState(false);
  const [attachmentAccessDenied, setAttachmentAccessDenied] = useState(false);
  const [showChangeWorkflowModal, setShowChangeWorkflowModal] = useState(false);
  const [targetWorkflowId, setTargetWorkflowId] = useState<string>('');
  const [isChangingWorkflow, setIsChangingWorkflow] = useState(false);
  const [editingCoreFields, setEditingCoreFields] = useState(false);
  const [coreObject, setCoreObject] = useState('');
  const [coreItemsDescription, setCoreItemsDescription] = useState('');

  const fetchPurchaseRequest = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}?include_subtasks=true`);
      setPurchaseRequest(response.data);
      setSubtasks(response.data?.subtasks || []);
    } catch (error) {
      console.error('Erro ao carregar PurchaseRequest:', error);
      setSubtasks([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Erro ao carregar comentários:', error);
    }
  }, [id]);

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/attachments/purchases/${id}`);
      setAttachments(response.data);
      setAttachmentAccessDenied(false);
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setAttachmentAccessDenied(true);
        setAttachments([]);
      }
    }
  }, [id]);

  const fetchSubtasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchases`, {
        params: {
          parent_request_id: id,
          limit: 1000,
          sort_by: 'created_at',
          sort_order: 'desc',
          // Subtarefas da requisição são histórico do item, não listagem geral.
          closed_visibility: 'all',
        },
      });
      setSubtasks(response.data || []);
    } catch (error: any) {
      console.error('Erro ao carregar subtarefas:', error);
      setSubtasks([]);
    }
  }, [id]);

  const fetchAvailableStatuses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}/available-statuses`);
      setAvailableStatuses(response.data.available_statuses || []);
      setCanAdvanceStepFromAPI(response.data.can_advance_step || false);
      setCanGoBackStepFromAPI(response.data.can_go_back_step || false);
      setCanConclude(response.data.can_conclude || false);
      setIsLastStep(response.data.is_last_step || false);
      setNextStepOptions(response.data.next_step_options || []);
      setMissingRequiredFields(response.data.missing_required_fields || []);
      setBlockedByPendingApprovalsApi(!!response.data.blocked_by_pending_approvals);
      setCanCancelPurchase(!!response.data.can_cancel);
    } catch (error: any) {
      // Ignorar erros 404 silenciosamente (endpoint pode não estar implementado)
      if (error.response?.status !== 404) {
        console.error('Erro ao carregar status disponíveis:', error);
      }
      setAvailableStatuses(['aberto', 'em_andamento', 'aguardando_informacao_usuario', 'aguardando_terceiros', 'aguardando_evidencia', 'aguardando_problema', 'aguardando_implantacao', 'resolvido', 'fechado']);
      setCanAdvanceStepFromAPI(false);
      setCanGoBackStepFromAPI(false);
      setCanConclude(false);
      setIsLastStep(false);
      setNextStepOptions([]);
      setMissingRequiredFields([]);
      setBlockedByPendingApprovalsApi(false);
      setCanCancelPurchase(false);
    }
  }, [id]);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
    }
  }, [id]);

  useEffect(() => {
    let cancelled = false;

    const loadSupplierNamesFromActivities = async () => {
      const ids = new Set<number>();
      activities.forEach((activity) => {
        extractSupplierIdsFromActivityValue(activity.old_value).forEach((id) => ids.add(id));
        extractSupplierIdsFromActivityValue(activity.new_value).forEach((id) => ids.add(id));
      });

      const missingIds = Array.from(ids).filter((id) => !supplierNamesById[id]);
      if (missingIds.length === 0) return;

      const resolvedEntries = await Promise.all(
        missingIds.map(async (supplierId) => {
          try {
            const supplier = await suppliersApi.getById(supplierId);
            const displayName = String(
              supplier.corporate_name ||
              supplier.trade_name ||
              ''
            ).trim();
            return [supplierId, displayName || `#${supplierId}`] as const;
          } catch {
            return [supplierId, `#${supplierId}`] as const;
          }
        })
      );

      if (cancelled) return;

      setSupplierNamesById((prev) => {
        const next = { ...prev };
        resolvedEntries.forEach(([supplierId, displayName]) => {
          next[supplierId] = displayName;
        });
        return next;
      });
    };

    loadSupplierNamesFromActivities();

    return () => {
      cancelled = true;
    };
  }, [activities, supplierNamesById]);

  const fetchpurchaseRequestTimes = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}/times`);
      setpurchaseRequestTimes(response.data);
      setTimesFetchedAtMs(Date.now());
      setNowMs(Date.now());
    } catch (error) {
      // Sem resposta os cards ficam em placeholder; registrar para não mascarar tempo zerado.
      console.error('Erro ao carregar tempos do PurchaseRequest:', error);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchPurchaseRequest();
      fetchComments();
      fetchAttachments();
      fetchSubtasks();
      fetchAvailableStatuses();
      fetchActivities();
      fetchpurchaseRequestTimes();
      fetchGroups(); // Sempre buscar grupos para verificar membro
      fetchWorkflows();
      if (userCanManageOpportunities(currentUser?.role)) {
        fetchUsers();
      }
      // fetchCustomFields e fetchCustomFieldValues serão chamados quando a requisição for carregada
    }
  }, [id, currentUser, fetchPurchaseRequest, fetchComments, fetchAttachments, fetchSubtasks, fetchAvailableStatuses, fetchActivities, fetchpurchaseRequestTimes]);

  useEffect(() => {
    if (!purchaseRequest) return;
    setCoreObject(purchaseRequest.object || '');
    setCoreItemsDescription(purchaseRequest.items_description || '');
  }, [purchaseRequest?.id, purchaseRequest?.object, purchaseRequest?.items_description]);

  const purchaseRequestStatus = purchaseRequest?.status;
  const isPurchaseTimerPaused = isPurchaseTimerPausedStatus(purchaseRequestStatus);
  const isPurchaseRequestClosed = isFinalStatus(purchaseRequestStatus || '');
  const isPurchaseClockRunning =
    !!purchaseRequestTimes && !isPurchaseRequestClosed && !isPurchaseTimerPaused;

  // Redesenhar a cada segundo enquanto o relógio corre. O tempo exibido é medido pelo relógio,
  // então um tick atrasado pelo navegador em aba de segundo plano não subtrai tempo.
  useEffect(() => {
    if (!isPurchaseClockRunning) return;
    const interval = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isPurchaseClockRunning]);

  // Ressincronizar com o backend periodicamente para o número exibido convergir para a verdade.
  useEffect(() => {
    if (!isPurchaseClockRunning) return;
    const interval = setInterval(() => {
      fetchpurchaseRequestTimes();
    }, PURCHASE_TIMES_RESYNC_MS);
    return () => clearInterval(interval);
  }, [isPurchaseClockRunning, fetchpurchaseRequestTimes]);

  // Recarregar tempos ao sair de pausa (ex.: após aprovação → aberto) para pegar novo timer de etapa
  useEffect(() => {
    if (id && purchaseRequestStatus && !isPurchaseTimerPaused) {
      fetchpurchaseRequestTimes();
    }
  }, [id, purchaseRequestStatus, isPurchaseTimerPaused, fetchpurchaseRequestTimes]);


  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      // Precisa de members para canAssign / membership no grupo atribuído
      const response = await axios.get(`${API_URL}/api/groups`, {
        params: { limit: 1000, include_members: true },
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflows`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Erro ao carregar fluxos:', error);
    }
  };

  const fetchCustomFields = useCallback(async () => {
    if (!id || !purchaseRequest?.workflow_id) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}/custom-fields/for-step`);
      setCustomFields(response.data || []);
      setProposalAccessDenied(false);
      setStageAccessDenied(false);
    } catch (error: any) {
      console.error('Erro ao carregar campos customizados:', error);
      setCustomFields([]);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setProposalAccessDenied(true);
        setStageAccessDenied(true);
      }
    }
  }, [id, purchaseRequest?.workflow_id]);

  const fetchCustomFieldValues = useCallback(async () => {
    if (!id) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/purchases/${id}/custom-fields`);
      setCustomFieldValues(response.data || []);
      setProposalAccessDenied(false);
      setStageAccessDenied(false);
    } catch (error: any) {
      console.error('Erro ao carregar valores dos campos customizados:', error);
      setCustomFieldValues([]);
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        setProposalAccessDenied(true);
        setStageAccessDenied(true);
      }
    }
  }, [id]);

  // Buscar campos customizados quando a requisição for carregada
  useEffect(() => {
    if (purchaseRequest && purchaseRequest.workflow_id) {
      fetchCustomFields();
      fetchCustomFieldValues();
    }
  }, [purchaseRequest, purchaseRequest?.id, purchaseRequest?.workflow_id, purchaseRequest?.current_step, fetchCustomFields, fetchCustomFieldValues]);

  useEffect(() => {
    const wfId = purchaseRequest?.workflow_id;
    if (!wfId) {
      setBoundWorkflow(null);
      return;
    }
    let cancelled = false;
    axios
      .get<Workflow>(`${API_URL}/api/workflows/${wfId}`)
      .then((res) => {
        if (!cancelled) setBoundWorkflow(res.data);
      })
      .catch(() => {
        if (!cancelled) setBoundWorkflow(null);
      });
    return () => {
      cancelled = true;
    };
  }, [purchaseRequest?.workflow_id]);

  const handleCustomFieldsSaved = useCallback(() => {
    fetchCustomFields();
    fetchCustomFieldValues();
    fetchPurchaseRequest();
    fetchAvailableStatuses();
  }, [fetchCustomFields, fetchCustomFieldValues, fetchPurchaseRequest, fetchAvailableStatuses]);

  useEffect(() => {
    const getOptionStepName = (option: {label?: string; step: string} | string): string => {
      if (typeof option === 'string') return option;
      return option.step || option.label || '';
    };

    if (nextStepOptions.length === 1) {
      setSelectedNextStep(getOptionStepName(nextStepOptions[0]));
      return;
    }

    if (nextStepOptions.length > 1) {
      const availableOptions = new Set(nextStepOptions.map(getOptionStepName).filter(Boolean));
      setSelectedNextStep((prev) => (prev && availableOptions.has(prev) ? prev : ''));
      return;
    }

    setSelectedNextStep('');
  }, [nextStepOptions]);

  const showAlert = (title: string, message: string, variant: 'success' | 'danger' | 'warning' | 'info' = 'info', details: string = '') => {
    setAlertModal({ title, message, variant, details });
    setShowAlertModal(true);
  };

  const handleTakePurchaseRequest = async () => {
    try {
      await axios.post(`${API_URL}/api/purchases/${id}/take`);
      await fetchPurchaseRequest();
      await fetchActivities();
      await fetchSubtasks();
      showAlert('Sucesso', 'Requisição assumida com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao assumir requisição', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const closeFinalReportModal = () => {
    setShowFinalReportModal(false);
    setFinalReportWithProposalCalculations(false);
  };

  const openFinalReportModal = async (withProposalCalculations = false) => {
    if (!purchaseRequest) return;
    setFinalReportWithProposalCalculations(withProposalCalculations);
    setShowFinalReportModal(true);
    setSelectedFinalReportFormat('xlsx');
    setIsLoadingFinalReportOptions(true);
    const reportPath = withProposalCalculations
      ? `${API_URL}/api/purchases/${purchaseRequest.id}/final-report/with-proposal-calculations`
      : `${API_URL}/api/purchases/${purchaseRequest.id}/final-report`;
    try {
      const response = await axios.get<PurchaseFinalReport>(reportPath);
      const preview = response.data;
      setFinalReportPreview(preview);
      setSelectedFinalReportFieldIds(preview.custom_fields.map((field) => field.custom_field_id));
    } catch (error: any) {
      console.error('Erro ao carregar opções do relatório final', error);
      if (withProposalCalculations && error?.response?.status === 400) {
        showAlert(
          'Aviso',
          formatApiErrorDetail(
            error?.response?.data?.detail,
            "Para gerar o Relatório com Cálculos, inclua um campo tabela chamado 'Proposta Vencedora'."
          ),
          'warning'
        );
        closeFinalReportModal();
        return;
      }
      showModalAlert({
        title: 'Erro',
        message: 'Não foi possível carregar os campos customizáveis do relatório final.',
        variant: 'danger',
      });
      closeFinalReportModal();
    } finally {
      setIsLoadingFinalReportOptions(false);
    }
  };

  const handleExportFinalReport = async () => {
    if (!purchaseRequest) return;
    setIsExportingFinalReport(true);
    try {
      const customFieldIdsParam = selectedFinalReportFieldIds.join(',');
      const exportFormat: 'xlsx' | 'pdf' = finalReportWithProposalCalculations
        ? 'xlsx'
        : selectedFinalReportFormat;
      const exportBasePath = finalReportWithProposalCalculations
        ? `${API_URL}/api/purchases/${purchaseRequest.id}/final-report/with-proposal-calculations/export`
        : `${API_URL}/api/purchases/${purchaseRequest.id}/final-report/export`;
      const response = await axios.get(
        `${exportBasePath}.${exportFormat}`,
        {
          responseType: 'blob',
          params: { custom_field_ids: customFieldIdsParam }
        }
      );

      const blob = new Blob([response.data], {
        type: exportFormat === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const reportFilePrefix = finalReportWithProposalCalculations
        ? `relatorio_final_contratacao_calculos_${purchaseRequest.id}`
        : `relatorio_final_contratacao_${purchaseRequest.id}`;
      link.download = `${reportFilePrefix}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      closeFinalReportModal();
    } catch (error: any) {
      console.error(`Erro ao exportar relatório final (${selectedFinalReportFormat})`, error);
      if (finalReportWithProposalCalculations && error?.response?.status === 400) {
        showAlert(
          'Aviso',
          formatApiErrorDetail(
            error?.response?.data?.detail,
            "Para gerar o Relatório com Cálculos, inclua um campo tabela chamado 'Proposta Vencedora'."
          ),
          'warning'
        );
        return;
      }
      showModalAlert({
        title: 'Erro',
        message: `Não foi possível exportar o relatório final em ${selectedFinalReportFormat.toUpperCase()}.`,
        variant: 'danger',
      });
    } finally {
      setIsExportingFinalReport(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    // Se o novo status for um dos status de aguardando, mostrar modal para solicitar waiting_reason (opcional)
    if (isWaitingStatus(newStatus)) {
      setPendingStatus(newStatus);
      setShowWaitingReasonModal(true);
      return;
    }

    if (newStatus === 'cancelado' && purchaseRequest?.status !== 'cancelado') {
      const ok = await showConfirm({
        title: 'Cancelar requisição',
        message:
          'Tem certeza que deseja cancelar esta requisição? Os aprovadores pendentes serão avisados e não poderão mais aprovar ou reprovar este item.',
        variant: 'danger',
        confirmLabel: 'Cancelar requisição',
        cancelLabel: 'Voltar',
      });
      if (!ok) return;
    }
    
    // Para outros status, atualizar diretamente
    try {
      setCancellingPurchase(newStatus === 'cancelado');
      await axios.put(`${API_URL}/api/purchases/${id}`, { status: newStatus });
      fetchPurchaseRequest();
      fetchAvailableStatuses();
      await fetchpurchaseRequestTimes();
      fetchActivities();
      showAlert(
        'Sucesso',
        newStatus === 'cancelado' ? 'Requisição cancelada com sucesso.' : 'Status atualizado com sucesso!',
        'success'
      );
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar status', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setCancellingPurchase(false);
    }
  };

  const handlePriorityChange = async (newPriority: string) => {
    try {
      await axios.put(`${API_URL}/api/purchases/${id}`, { priority: newPriority });
      fetchPurchaseRequest();
      showAlert('Sucesso', 'Prioridade atualizada com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar prioridade', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleUrgencyChange = async (newUrgency: TicketUrgency) => {
    try {
      await axios.put(`${API_URL}/api/purchases/${id}`, { urgency: newUrgency });
      fetchPurchaseRequest();
      showAlert('Sucesso', 'Urgência atualizada com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar urgência', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleConfirmWaitingStatus = async () => {
    // waiting_reason é opcional agora, pois o status já indica o motivo
    try {
      await axios.put(`${API_URL}/api/purchases/${id}`, { 
        status: pendingStatus,
        waiting_reason: waitingReason?.trim() || ''
      });
      fetchPurchaseRequest();
      fetchAvailableStatuses();
      await fetchpurchaseRequestTimes();
      setShowWaitingReasonModal(false);
      setWaitingReason('');
      setPendingStatus('');
      showAlert('Sucesso', `Status atualizado para ${getStatusLabel(pendingStatus)} com sucesso!`, 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atualizar status', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleAdvanceStep = async (selectedStep?: string) => {
    const getOptionStepName = (option: {label?: string; step: string} | string): string => {
      if (typeof option === 'string') return option;
      return option.step || option.label || '';
    };

    let stepName: string | undefined;
    if (selectedStep) {
      stepName = selectedStep;
    } else if (nextStepOptions.length === 1) {
      stepName = getOptionStepName(nextStepOptions[0]);
    }

    if (nextStepOptions.length > 1 && !stepName) {
      showAlert('Atenção', 'Selecione o próximo passo para avançar o fluxo.', 'warning');
      return;
    }

    if (!stepName && nextStepOptions.length === 0) {
      showAlert('Erro', 'Não há próximo passo disponível', 'danger');
      return;
    }
    
    setIsAdvancingStep(true);
    try {
      const finalStepName = typeof stepName === 'string' ? stepName : String(stepName || '');
      const params = finalStepName ? { next_step: finalStepName } : {};
      await axios.post(`${API_URL}/api/purchases/${id}/advance-step`, null, { params });
      await fetchPurchaseRequest();
      await fetchAvailableStatuses();
      await fetchActivities();
      await fetchpurchaseRequestTimes();
      showAlert('Sucesso', 'Passo do fluxo avançado com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao avançar passo', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsAdvancingStep(false);
    }
  };

  const handleConclude = async () => {
    setIsConcluding(true);
    try {
      await axios.post(`${API_URL}/api/purchases/${id}/conclude`);
      await fetchPurchaseRequest();
      await fetchAvailableStatuses();
      await fetchActivities();
      await fetchpurchaseRequestTimes();
      showAlert('Sucesso', 'Requisição concluída com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao concluir requisição', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsConcluding(false);
    }
  };

  const handleGoBackStep = async () => {
    setIsGoingBackStep(true);
    try {
      await axios.post(`${API_URL}/api/purchases/${id}/go-back-step`);
      await fetchPurchaseRequest();
      await fetchAvailableStatuses();
      await fetchActivities();
      await fetchpurchaseRequestTimes();
      showAlert('Sucesso', 'Passo do fluxo retrocedido com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao retroceder passo', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsGoingBackStep(false);
    }
  };

  const handleAssign = async (assignedTo?: number, assignedGroupId?: number) => {
    try {
      await axios.post(`${API_URL}/api/purchases/${id}/assign`, null, {
        params: { assigned_to: assignedTo, assigned_group_id: assignedGroupId }
      });
      await fetchPurchaseRequest();
      await fetchpurchaseRequestTimes();
      await fetchActivities();
      setShowAssignModal(false);
      showAlert('Sucesso', 'Requisição atribuída com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao atribuir requisição', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleAddComment = async (content: string, isInternalParam: boolean) => {
    if (isAddingComment) {
      return;
    }
    const trimmed = (content || '').trim();
    if (!trimmed) {
      showAlert('Atenção', 'Informe o texto do comentário antes de enviar.', 'warning');
      return;
    }
    setIsAddingComment(true);
    try {
      await axios.post(`${API_URL}/api/purchases/${id}/comments`, {
        content: trimmed,
        is_internal: isInternalParam,
      });
      setCommentText('');
      setIsInternal(false);
      setShowCommentModal(false);
      await fetchComments();
      await fetchActivities();
      showAlert('Sucesso', 'Comentário adicionado com sucesso!', 'success');
    } catch (error: any) {
      console.error('Erro ao adicionar comentário:', error);
      showAlert('Erro', 'Erro ao adicionar comentário', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleCreateSubtask = async ({
    title,
    description,
    priority,
    subtaskTypeId,
    targetType,
    assignedTo,
  }: PurchaseSubtaskSubmitPayload) => {
    // Proteção contra duplo clique - retornar se já estiver processando
    if (isCreatingSubtask) {
      return;
    }
    
    setIsCreatingSubtask(true);
    
    try {
      const requestData: Record<string, unknown> = {
        title,
        description,
        parent_request_id: parseInt(id || '0'),
        subtask_target_type: targetType,
      };
      if (targetType === 'user' && assignedTo) {
        requestData.assigned_to = assignedTo;
      }
      if (canManagePurchasePriority(currentUser)) {
        requestData.priority = priority;
      }

      // Se houver tipo de subtarefa selecionado, buscar o workflow associado
      if (subtaskTypeId && purchaseRequest?.workflow_id) {
        try {
          const subtaskTypesResponse = await axios.get(`${API_URL}/api/workflows/${purchaseRequest.workflow_id}/subtask-types`);
          const selectedType = subtaskTypesResponse.data.find((t: any) => t.id === subtaskTypeId);
          if (selectedType?.workflow_id_for_subtask) {
            requestData.workflow_id = selectedType.workflow_id_for_subtask;
          }
        } catch (error) {
          console.warn('Erro ao buscar tipo de subtarefa, continuando sem workflow específico:', error);
        }
      }
      
      const response = await axios.post(`${API_URL}/api/purchases/`, requestData);
      
      console.log('Subtarefa criada:', response.data);
      
      // Aguardar um pouco antes de recarregar para garantir que o backend processou
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Recarregar subtarefas
      await fetchSubtasks();
      // Também recarregar a requisição para garantir sincronização
      await fetchPurchaseRequest();
      
      setShowSubtaskModal(false);
      showAlert('Sucesso', 'Subtarefa criada com sucesso!', 'success');
    } catch (error: any) {
      console.error('Erro ao criar subtarefa:', error);
      showAlert('Erro', 'Erro ao criar subtarefa', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setIsCreatingSubtask(false);
    }
  };

  const handleFileUpload = async () => {
    if (!attachmentFile) {
      showAlert('Aviso', 'Por favor, selecione um arquivo', 'warning');
      return;
    }

    const formData = new FormData();
    formData.append('file', attachmentFile);
    if (attachmentDescription) {
      formData.append('description', attachmentDescription);
    }
    if (canManageOpportunities) {
      formData.append('visible_to_requester', attachmentVisibleToRequester ? 'true' : 'false');
    }

    try {
      await axios.post(`${API_URL}/api/attachments/purchases/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchAttachments();
      setShowAttachmentModal(false);
      setAttachmentFile(null);
      setAttachmentDescription('');
      setAttachmentVisibleToRequester(false);
      showAlert('Sucesso', 'Anexo adicionado com sucesso!', 'success');
    } catch (error: any) {
      showAlert('Erro', 'Erro ao fazer upload do anexo', 'danger', formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleToggleAttachmentRequesterVisibility = async (
    attachment: PurchaseAttachment,
    visibleToRequester: boolean
  ) => {
    if (!canManageOpportunities || !id || attachment.id <= 0) return;
    try {
      setUpdatingAttachmentVisibilityId(attachment.id);
      await axios.patch(
        `${API_URL}/api/attachments/purchases/${id}/${attachment.id}/requester-visibility`,
        { visible_to_requester: visibleToRequester }
      );
      await fetchAttachments();
      showAlert('Sucesso', 'Visibilidade do anexo atualizada.', 'success');
    } catch (error: any) {
      showAlert(
        'Erro',
        'Não foi possível atualizar a visibilidade do anexo.',
        'danger',
        formatApiErrorDetail(error.response?.data?.detail)
      );
    } finally {
      setUpdatingAttachmentVisibilityId(null);
    }
  };

  const handleDeleteAttachment = async (attachment: PurchaseAttachment) => {
    if (!id) return;
    const confirmed = await showConfirm({
      title: 'Excluir anexo',
      message: `Tem certeza que deseja excluir o anexo "${attachment.filename}"?`,
      variant: 'warning',
      confirmLabel: 'Excluir anexo',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;
    try {
      setDeletingAttachmentId(attachment.id);
      await axios.delete(`${API_URL}/api/purchases/${id}/attachments/${attachment.id}`);
      await fetchAttachments();
      await fetchPurchaseRequest();
      await fetchAvailableStatuses();
      await fetchActivities();
      showAlert('Sucesso', 'Anexo removido com sucesso.', 'success');
    } catch (error: any) {
      showAlert(
        'Erro',
        'Não foi possível remover o anexo.',
        'danger',
        formatApiErrorDetail(error.response?.data?.detail),
      );
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleSaveCoreFields = async () => {
    if (!id || !purchaseRequest) return;
    const objectValue = String(coreObject || '').trim();
    const itemsValue = String(coreItemsDescription || '').trim();
    if (!objectValue) {
      showAlert('Atenção', 'O campo Objeto é obrigatório.', 'warning');
      return;
    }
    if (!itemsValue) {
      showAlert('Atenção', 'O campo Descrição dos Itens é obrigatório.', 'warning');
      return;
    }
    try {
      setEditingCoreFields(true);
      await axios.put(`${API_URL}/api/purchases/${id}`, {
        object: objectValue,
        items_description: itemsValue,
      });
      await fetchPurchaseRequest();
      await fetchAvailableStatuses();
      await fetchActivities();
      showAlert('Sucesso', 'Informações do processo atualizadas com sucesso.', 'success');
    } catch (error: any) {
      showAlert(
        'Erro',
        'Não foi possível atualizar Objeto e Descrição dos Itens.',
        'danger',
        formatApiErrorDetail(error.response?.data?.detail),
      );
    } finally {
      setEditingCoreFields(false);
    }
  };

  const handleConfirmWorkflowChange = async () => {
    if (!id || !targetWorkflowId) return;
    try {
      setIsChangingWorkflow(true);
      await axios.post(`${API_URL}/api/purchases/${id}/change-workflow`, {
        workflow_id: Number(targetWorkflowId),
      });
      await fetchPurchaseRequest();
      await fetchCustomFields();
      await fetchCustomFieldValues();
      await fetchAvailableStatuses();
      await fetchActivities();
      setShowChangeWorkflowModal(false);
      showAlert('Sucesso', 'Tipo de contratação alterado com sucesso.', 'success');
    } catch (error: any) {
      showAlert(
        'Erro',
        'Não foi possível alterar o tipo de contratação.',
        'danger',
        formatApiErrorDetail(error.response?.data?.detail),
      );
    } finally {
      setIsChangingWorkflow(false);
    }
  };


  const handleApprove = async (approval?: PurchaseApproval) => {
    if (!purchaseRequest) return;
    const questions = parseApproverQuestions(approval?.approver_questions);
    if (questions.length > 0) {
      const validationError = validateApproverAnswers(questions, approverAnswers);
      if (validationError) {
        showAlert('Atenção', validationError, 'warning');
        return;
      }
    }
    try {
      setProcessingApproval(true);
      const requestPayload: Record<string, unknown> = {};
      if (approval?.id) {
        requestPayload.approval_id = approval.id;
      }
      if (questions.length > 0) {
        requestPayload.answers = buildApproverAnswersPayload(questions, approverAnswers);
      }
      const response = await axios.post(`${API_URL}/api/purchases/${purchaseRequest.id}/approve`, requestPayload);
      console.log('Resposta da aprovação:', response.data);
      showAlert('Sucesso', 'Requisição aprovada com sucesso!', 'success');
      setShowApproveModal(false);
      setApproverAnswers({});
      await fetchPurchaseRequest();
      setApprovalInAction(null);
    } catch (error: any) {
      console.error('Erro completo na aprovação:', error);
      console.error('Status:', error.response?.status);
      console.error('Data:', error.response?.data);
      // Se o status for 200, mas ainda assim caiu no catch, pode ser um problema de parsing
      if (error.response?.status === 200 || (error.response?.status >= 200 && error.response?.status < 300)) {
        // A requisição foi bem-sucedida, mas houve um problema no parsing
        showAlert('Sucesso', 'Requisição aprovada com sucesso!', 'success');
        await fetchPurchaseRequest();
      } else {
        showAlert('Erro', formatApiErrorDetail(error.response?.data?.detail, 'Não foi possível aprovar a requisição.'), 'danger');
      }
    } finally {
      setProcessingApproval(false);
    }
  };

  // Perguntas adicionais exigem modal também na aprovação; sem elas, mantém o clique único.
  const openApproveFlow = (approval?: PurchaseApproval) => {
    if (!approval) return;
    if (parseApproverQuestions(approval.approver_questions).length === 0) {
      handleApprove(approval);
      return;
    }
    setApprovalInAction(approval);
    setApproverAnswers({});
    setShowApproveModal(true);
  };

  const openRejectFlow = (approval?: PurchaseApproval) => {
    if (!approval) return;
    setApprovalInAction(approval);
    setRejectionJustification('');
    setApproverAnswers({});
    setShowRejectModal(true);
  };

  const handleReject = async () => {
    if (!purchaseRequest || !rejectionJustification.trim()) {
      showAlert('Atenção', 'Por favor, informe a justificativa da reprovação.', 'warning');
      return;
    }
    const questions = parseApproverQuestions(approvalInAction?.approver_questions);
    if (questions.length > 0) {
      const validationError = validateApproverAnswers(questions, approverAnswers);
      if (validationError) {
        showAlert('Atenção', validationError, 'warning');
        return;
      }
    }
    try {
      setProcessingApproval(true);
      await axios.post(`${API_URL}/api/purchases/${purchaseRequest.id}/reject`, {
        approval_id: approvalInAction?.id,
        justification: rejectionJustification.trim(),
        ...(questions.length > 0
          ? { answers: buildApproverAnswersPayload(questions, approverAnswers) }
          : {}),
      });
      showAlert('Sucesso', 'Requisição reprovada. O requisitante foi notificado.', 'success');
      setShowRejectModal(false);
      setRejectionJustification('');
      setApproverAnswers({});
      setApprovalInAction(null);
      await fetchPurchaseRequest();
    } catch (error: any) {
      showAlert('Erro', formatApiErrorDetail(error.response?.data?.detail, 'Não foi possível reprovar a requisição.'), 'danger');
    } finally {
      setProcessingApproval(false);
    }
  };

  const isApprover = (): boolean => {
    if (!purchaseRequest || !currentUser) return false;
    // Verificar se o usuário é o aprovador principal
    if (purchaseRequest.approver_id === currentUser.id) return true;
    // Verificar se o usuário é um aprovador de campos personalizados (através das aprovações)
    if (purchaseRequest.approvals && purchaseRequest.approvals.some(a => a.approver_id === currentUser.id)) {
      return true;
    }
    return false;
  };

  const formatTimeMinutesSeconds = (seconds: number): string => {
    const totalSeconds = Math.floor(seconds);
    if (totalSeconds >= 86400) {
      // 24h ou mais: Xd Yh Zmin Ws
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      const parts = [`${days}d`];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}min`);
      parts.push(`${secs}s`);
      return parts.join(' ');
    }
    if (totalSeconds >= 3600) {
      // 1h a menos de 24h: Xh Ymin Zs
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      const parts = [`${hours}h`];
      if (minutes > 0) parts.push(`${minutes}min`);
      parts.push(`${secs}s`);
      return parts.join(' ');
    }
    // Menos de 1h: Xmin Ys
    const totalMinutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${totalMinutes}min ${remainingSeconds}s`;
  };

  if (loading) {
    return <div className="loading-spinner">Carregando...</div>;
  }

  if (!purchaseRequest) {
    return <div>Requisição não encontrada</div>;
  }

  const parentRequestId =
    purchaseRequest.parent_request_id ?? purchaseRequest.parent_purchase_request_id;
  const purchasePublicId = purchaseRequest.public_id || String(purchaseRequest.id);
  const currentWorkflowForAccess =
    boundWorkflow?.id === purchaseRequest.workflow_id
      ? boundWorkflow
      : workflows.find((w) => w.id === purchaseRequest.workflow_id) ?? boundWorkflow ?? undefined;
  const workflowStepsForAccess = parseWorkflowStepNames(currentWorkflowForAccess?.steps);
  const isInitialStepForAccess = isInitialPurchaseStep(
    purchaseRequest.current_step,
    workflowStepsForAccess,
  );

  const workflowBlockedByApprovals =
    blockedByPendingApprovalsApi ||
    purchaseRequest.status === 'aguardando_aprovacao' ||
    purchaseRequest.status === 'aguardando_reenvio' ||
    (purchaseRequest.approvals?.some((a: PurchaseApproval) => a.status === 'pendente') ?? false);
  const isOwnerForSensitive =
    !!currentUser &&
    (purchaseRequest.created_by === currentUser.id || purchaseRequest.requester_id === currentUser.id);

  const hasRejectedApproval =
    purchaseRequest.approvals?.some((a) => a.status === 'reprovado') ?? false;
  const hasApprovedApproval =
    purchaseRequest.approvals?.some((a) => a.status === 'aprovado') ?? false;
  const hasPendingApproval =
    purchaseRequest.approvals?.some((a) => a.status === 'pendente') ?? false;
  const isReturnedForRequesterReviewCycle =
    purchaseRequest.status === 'aguardando_reenvio' &&
    Boolean(purchaseRequest.requester_review_return_step);
  const canResubmitForApproval =
    purchaseRequest.status === 'aguardando_reenvio' &&
    isOwnerForSensitive &&
    (hasRejectedApproval || isReturnedForRequesterReviewCycle);

  const handleResubmitForApproval = async () => {
    try {
      setResubmittingApproval(true);
      await axios.post(`${API_URL}/api/purchases/${purchaseRequest.id}/resubmit-for-approval`);
      showAlert(
        'Sucesso',
        'Requisição reenviada para aprovação. Os aprovadores serão notificados.',
        'success'
      );
      await fetchPurchaseRequest();
    } catch (error: any) {
      showAlert(
        'Erro',
        formatApiErrorDetail(
          error.response?.data?.detail,
          'Não foi possível reenviar a requisição para aprovação.'
        ),
        'danger'
      );
    } finally {
      setResubmittingApproval(false);
    }
  };

  const handleReturnToRequesterReview = async () => {
    const confirmed = await showConfirm({
      title: 'Retornar para revisão do requisitante',
      message:
        'Confirma o retorno desta requisição para revisão do requisitante? A etapa atual será pausada e o processo seguirá para aguardando reenvio até novo envio para aprovação.',
      variant: 'warning',
      confirmLabel: 'Confirmar retorno',
      cancelLabel: 'Cancelar',
    });
    if (!confirmed) return;

    try {
      setReturningForRequesterReview(true);
      await axios.post(`${API_URL}/api/purchases/${purchaseRequest.id}/return-to-requester-review`);
      showAlert(
        'Sucesso',
        'Requisição retornada para revisão do requisitante. O SLA foi pausado até o reenvio.',
        'success'
      );
      await fetchPurchaseRequest();
    } catch (error: any) {
      showAlert(
        'Erro',
        formatApiErrorDetail(
          error.response?.data?.detail,
          'Não foi possível retornar a requisição para revisão do requisitante.'
        ),
        'danger'
      );
    } finally {
      setReturningForRequesterReview(false);
    }
  };

  const isAssignedForSensitive =
    !!currentUser &&
    (purchaseRequest.assigned_to === currentUser.id || purchaseRequest.taken_by === currentUser.id);
  const isInAssignedGroupForSensitive =
    !!currentUser &&
    !!purchaseRequest.assigned_group_id &&
    groups.some((group) =>
      group.id === purchaseRequest.assigned_group_id &&
      !!group.members?.some((member) => member.user_id === currentUser.id)
    );
  const isApproverForSensitive = isApprover();
  const isSubtaskRequest = !!parentRequestId;
  const isIndividualSubtask =
    purchaseRequest.is_individual_subtask ??
    (isSubtaskRequest && !!purchaseRequest.assigned_to && !purchaseRequest.assigned_group_id);
  // Responsável individual opera a própria subtarefa independentemente do cargo.
  const isIndividualSubtaskOwner =
    isIndividualSubtask && !!currentUser && purchaseRequest.assigned_to === currentUser.id;
  const canOpenParentRequest = purchaseRequest.can_access_parent !== false;
  const isAnalystOrAdmin =
    !!currentUser &&
    (currentUser.role === 'admin' ||
      currentUser.role === 'gerente' ||
      currentUser.role === 'analista');
  const isReturnReviewActor =
    !!currentUser && (currentUser.role === 'admin' || currentUser.role === 'analista');
  const canReturnToRequesterReview =
    isReturnReviewActor &&
    !isOwnerForSensitive &&
    !isFinalStatus(purchaseRequest.status) &&
    purchaseRequest.status !== 'aguardando_reenvio' &&
    !isInitialStepForAccess &&
    hasApprovedApproval &&
    !hasPendingApproval;
  const isAdminForAccess =
    currentUser?.role === 'admin' || currentUser?.role === 'gerente';
  const isOwnerForAccess =
    !!currentUser &&
    (purchaseRequest.created_by === currentUser.id || purchaseRequest.requester_id === currentUser.id);
  const isAssignedForAccess =
    !!currentUser &&
    (purchaseRequest.assigned_to === currentUser.id || purchaseRequest.taken_by === currentUser.id);
  const isInAssignedGroupForAccess =
    !!currentUser &&
    !!purchaseRequest.assigned_group_id &&
    groups.some(
      (group) =>
        group.id === purchaseRequest.assigned_group_id &&
        !!group.members?.some((member) => member.user_id === currentUser.id)
    );
  const isApproverForAccess = isApproverForSensitive;
  const canUserAccessPurchaseForFinalReport = (() => {
    if (!currentUser) return false;
    if (currentUser.role === 'usuario' || currentUser.role === 'gerente') {
      // Espelha can_user_access_purchase_request no backend para usuário comum/gerente.
      return isOwnerForAccess || isAssignedForAccess || isApproverForAccess;
    }
    return (
      isAdminForAccess ||
      isOwnerForAccess ||
      isAssignedForAccess ||
      isInAssignedGroupForAccess ||
      isApproverForAccess
    );
  })();
  const canShowFinalReportButton =
    canUserAccessPurchaseForFinalReport;
  const canShowFinalReportWithCalculationsButton =
    canShowFinalReportButton && currentArea === 'purchases';
  const canViewFullStageContent =
    !!currentUser &&
    (
      (isOwnerForSensitive &&
        (purchaseRequest.status === 'rascunho' || isInitialStepForAccess)) ||
      (!isOwnerForSensitive && isAnalystOrAdmin) ||
      isApproverForSensitive ||
      (isSubtaskRequest && (isAssignedForSensitive || isInAssignedGroupForSensitive)) ||
      (
        !isSubtaskRequest &&
        (isOwnerForSensitive || isAssignedForSensitive) &&
        isInitialStepForAccess
      )
    );
  const isApproverOnlyForEdit =
    isApproverForSensitive &&
    !isAdminForAccess &&
    !isOwnerForSensitive &&
    !isAssignedForSensitive &&
    !isInAssignedGroupForSensitive &&
    !(currentUser?.role === 'analista' && isInAssignedGroupForAccess);
  const canEditDuringApprovalPeriod =
    isAdminForAccess ||
    (
      isOwnerForSensitive &&
      (
        purchaseRequest.status === 'aguardando_reenvio' ||
        (purchaseRequest.status === 'aguardando_aprovacao' && isInitialStepForAccess)
      )
    );
  const canEditPurchaseContent =
    !!purchaseRequest &&
    !!currentUser &&
    !isFinalStatus(purchaseRequest.status) &&
    (
      workflowBlockedByApprovals
        ? canEditDuringApprovalPeriod
        : (
            !isApproverOnlyForEdit &&
            (
              canViewFullStageContent ||
              (isOwnerForSensitive && purchaseRequest.status === 'aguardando_reenvio')
            )
          )
    );
  const canViewSensitiveProposals =
    !!currentUser &&
    (
      isAnalystOrAdmin ||
      isIndividualSubtaskOwner ||
      (
        isInitialStepForAccess &&
        (isOwnerForSensitive || isAssignedForSensitive)
      ) ||
      isApproverForSensitive
    );
  const fieldById = new Map<number, WorkflowCustomField>(customFields.map((field) => [field.id, field]));
  const hasProposalFields = customFields.some((field) => isProposalRelatedField(field));
  const hasProposalCustomValue = customFieldValues.some((value) =>
    isProposalRelatedField(fieldById.get(value.custom_field_id))
  );
  const hasProposalAttachments = attachments.some((attachment) =>
    isProposalLikeAttachment(attachment.filename, attachment.description)
  );
  const isProposalFlowStep = isProposalStage(purchaseRequest.current_step);
  const shouldEnforceProposalRestriction =
    isProposalFlowStep || hasProposalFields || hasProposalCustomValue || hasProposalAttachments;
  const showSensitiveProposalContent = !shouldEnforceProposalRestriction || canViewSensitiveProposals;

  // Verificar se o usuário pode distribuir PurchaseRequests (apenas líderes e admins)
  const canAssign = (): boolean => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    
    // Verificar se é líder de algum grupo
    if (groups.some(g => g.members?.some(m => m.user_id === currentUser?.id && m.is_leader))) {
      return true;
    }
    
    return false;
  };

  // Verificar se o usuário pode criar subtarefas (apenas analista responsável pela requisição)
  const canCreateSubtask = (): boolean => {
    if (!currentUser || !purchaseRequest) return false;

    const isRequesterOwner =
      purchaseRequest.created_by === currentUser.id ||
      purchaseRequest.requester_id === currentUser.id;
    if (isRequesterOwner) return false;
    
    // Admin pode sempre criar subtarefas
    if (currentUser.role === 'admin') return true;
    
    // Apenas analistas podem criar subtarefas
    if (currentUser.role !== 'analista') return false;
    
    // Verificar se é responsável pela requisição (assigned_to ou taken_by)
    const isResponsible = purchaseRequest.assigned_to === currentUser.id || purchaseRequest.taken_by === currentUser.id;
    
    return isResponsible;
  };

  // Verificar se o usuário pode assumir a requisição
  const canTakePurchaseRequest = (): boolean => {
    if (!currentUser || !purchaseRequest) return false;

    // Não pode assumir se já foi assumido pelo usuário atual
    if (purchaseRequest.taken_by === currentUser.id) return false;
    
    // Não pode assumir se já está atribuído diretamente ao usuário atual
    if (purchaseRequest.assigned_to === currentUser.id) return false;
    
    // Admin sempre pode assumir se a requisição está atribuída a um grupo
    if (currentUser.role === 'admin' && purchaseRequest.assigned_group_id) return true;
    
    // Se está atribuído a um grupo, verificar se o usuário pertence ao grupo
    if (purchaseRequest.assigned_group_id) {
      const assignedGroup = groups.find(g => g.id === purchaseRequest.assigned_group_id);
      if (assignedGroup && assignedGroup.members) {
        const isMember = assignedGroup.members.some(m => m.user_id === currentUser?.id);
        return isMember;
      }
    }
    
    return false;
  };

  // Verificar se pode avançar o passo
  const canAdvanceStep = (): boolean => {
    if (!currentUser || !purchaseRequest) return false;
    if (workflowBlockedByApprovals) return false;

    // Não pode avançar se a requisição já está fechada
    if (isFinalStatus(purchaseRequest.status)) return false;
    
    // Verificar permissões primeiro
    const hasPermission = currentUser.role === 'admin' || 
                          purchaseRequest.assigned_to === currentUser.id || 
                          purchaseRequest.taken_by === currentUser.id;
    
    if (!hasPermission) return false;
    
    // Se há um workflow, confiar no backend que já verificou tudo
    if (purchaseRequest.workflow_id) {
      // O backend retorna can_advance_step baseado na lógica do workflow
      return canAdvanceStepFromAPI === true;
    }
    
    // Se não há workflow, permitir avançar sempre (permite mudança manual de status)
    return true;
  };

  // Tempo total e tempo da etapa atual: base do backend mais os segundos decorridos desde a
  // resposta, e só quando o próprio backend indica que algum timer está correndo.
  const displayTimes = resolvePurchaseDisplayTimes({
    times: purchaseRequestTimes,
    currentStep: purchaseRequest?.current_step,
    isFinal: isPurchaseRequestClosed,
    isPaused: isPurchaseTimerPaused,
    fetchedAtMs: timesFetchedAtMs,
    nowMs,
  });
  const liveOverlaySeconds = elapsedSecondsSince(timesFetchedAtMs, nowMs);
  // Preferir o fluxo carregado por ID (alinhado à requisição); a lista GET /workflows pode ser
  // deduplicada/diferente e ficar desatualizada em relação a GET /workflows/:id.
  const currentWorkflow =
    boundWorkflow?.id === purchaseRequest.workflow_id
      ? boundWorkflow
      : workflows.find((w) => w.id === purchaseRequest.workflow_id) ?? boundWorkflow ?? undefined;
  const workflowSteps = (() => {
    if (!currentWorkflow?.steps) return [] as string[];
    try {
      const stepsData = JSON.parse(currentWorkflow.steps);
      if (Array.isArray(stepsData)) {
        return stepsData.map((s: any) => typeof s === 'string' ? s : (s.name || String(s))).filter(Boolean);
      }
      if (stepsData?.steps && Array.isArray(stepsData.steps)) {
        return stepsData.steps.map((s: any) => typeof s === 'string' ? s : (s.name || String(s))).filter(Boolean);
      }
      return [];
    } catch {
      return currentWorkflow.steps.split(',').map((s: string) => s.trim()).filter(Boolean);
    }
  })();

  const displayStepOrderBase = (purchaseRequestTimes?.time_in_steps || []).reduce((acc: string[], stepTime) => {
    const stepName = String(stepTime.step_name || '').trim();
    if (!stepName) return acc;
    const stepNameNorm = stepName.toLowerCase();
    if (acc.some((existing) => existing.toLowerCase() === stepNameNorm)) return acc;
    acc.push(stepName);
    return acc;
  }, []);
  const displayStepOrder = [...displayStepOrderBase];
  const firstWorkflowStep = workflowSteps[0];
  if (firstWorkflowStep && !displayStepOrder.some((s) => s.toLowerCase() === firstWorkflowStep.toLowerCase())) {
    displayStepOrder.unshift(firstWorkflowStep);
  }
  if (purchaseRequest.current_step && !displayStepOrder.some((s) => s.toLowerCase() === purchaseRequest.current_step!.toLowerCase())) {
    displayStepOrder.push(purchaseRequest.current_step);
  }
  const canManageOpportunities = userCanManageOpportunities(currentUser?.role);
  const canManageAttachmentVisibility = canManageOpportunities && !isOwnerForSensitive;
  const canUploadPurchaseAttachment = canManageOpportunities || isIndividualSubtaskOwner;
  const showOpportunityPreparationAction =
    canManageOpportunities && isOpportunityPreparationStep(purchaseRequest.current_step);

  const openOpportunityPreparation = () => {
    navigate('/purchases/opportunities', {
      state: {
        openCreate: true,
        sourcePurchaseId: purchaseRequest.id,
        sourcePurchasePublicId: purchasePublicId,
        suggestedTitle: `Oportunidade da requisição #${purchasePublicId}`,
      },
    });
  };

  const canSeeInternalPriority = canManagePurchasePriority(currentUser);
  const canEditAsRequesterOnResubmit =
    isOwnerForSensitive && purchaseRequest.status === 'aguardando_reenvio';
  const canEditUrgencyAndPriority =
    PURCHASE_CLASSIFICATION_EDIT_STATUSES.has(purchaseRequest.status) &&
    (
      workflowBlockedByApprovals
        ? canEditDuringApprovalPeriod
        : (
            (canSeeInternalPriority || canEditAsRequesterOnResubmit) &&
            (
              currentUser?.role === 'admin' ||
              purchaseRequest.assigned_to === currentUser?.id ||
              purchaseRequest.taken_by === currentUser?.id ||
              canEditAsRequesterOnResubmit
            )
          )
    );
  const canChangeWorkflow =
    !!purchaseRequest.workflow_id &&
    !isSubtaskRequest &&
    canEditPurchaseContent &&
    (isOwnerForSensitive || isAdminForAccess) &&
    (isInitialStepForAccess || purchaseRequest.status === 'aguardando_reenvio');
  const purchaseWorkflowOptions = workflows.filter(
    (workflow) =>
      workflow.id !== purchaseRequest.workflow_id &&
      workflow.is_active &&
      (workflow.area_type || '').toLowerCase() === 'purchases',
  );
  const renderProcessInfoHeader = () => (
    <div className="d-flex justify-content-between align-items-center gap-2 flex-wrap">
      <h5 className="mb-0">Informações do Processo</h5>
      {canChangeWorkflow && (
        <Button
          className="purchase-change-workflow-btn"
          size="sm"
          variant="outline-primary"
          onClick={() => {
            setTargetWorkflowId('');
            setShowChangeWorkflowModal(true);
          }}
        >
          Alterar tipo de contratação
        </Button>
      )}
    </div>
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button variant="outline-secondary" onClick={handleNavigateBack}>
          Voltar
        </Button>
        <h2>Requisição #{purchasePublicId} {parentRequestId && <small className="text-muted">(Subtarefa)</small>}</h2>
        <div className="d-flex gap-2 align-items-center">
          <Badge bg={getUrgencyColor(purchaseRequest.urgency)}>
            Urgência: {getUrgencyLabel(purchaseRequest.urgency)}
          </Badge>
          {canSeeInternalPriority && purchaseRequest.priority && (
            <Badge bg={getPriorityColor(purchaseRequest.priority)}>
              Prioridade: {getPriorityLabel(purchaseRequest.priority)}
            </Badge>
          )}

          {/* Seletor de status (igual ao anterior). Extra: com aprovação pendente, quem pode cancelar vê só Cancelado. */}
          {(() => {
            const isStatusManager =
              currentUser?.role === 'admin' ||
              purchaseRequest.assigned_to === currentUser?.id ||
              purchaseRequest.taken_by === currentUser?.id;
            const showFullStatusSelect =
              isStatusManager && !isFinalStatus(purchaseRequest.status) && !workflowBlockedByApprovals;
            const showCancelViaStatus =
              !showFullStatusSelect &&
              canCancelPurchase &&
              !isFinalStatus(purchaseRequest.status) &&
              workflowBlockedByApprovals;

            if (showFullStatusSelect) {
              return (
                <Form.Select
                  value={purchaseRequest.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  style={{ width: 'auto', display: 'inline-block' }}
                  disabled={cancellingPurchase}
                >
                  {availableStatuses.map((status) => (
                    <option key={status} value={status}>
                      {getStatusLabel(status)}
                    </option>
                  ))}
                </Form.Select>
              );
            }

            if (showCancelViaStatus) {
              return (
                <Form.Select
                  value={purchaseRequest.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  style={{ width: 'auto', display: 'inline-block' }}
                  disabled={cancellingPurchase}
                >
                  {Array.from(new Set([purchaseRequest.status, 'cancelado'])).map((status) => (
                    <option key={status} value={status}>
                      {getStatusLabel(status)}
                    </option>
                  ))}
                </Form.Select>
              );
            }

            return (
              <Badge bg={getStatusColor(purchaseRequest.status)}>
                {getStatusLabel(purchaseRequest.status)}
              </Badge>
            );
          })()}
        </div>
      </div>

      {purchaseRequest.status === 'cancelado' && (
        <Alert variant="warning" className="mb-3">
          <strong>Requisição cancelada.</strong>{' '}
          {(() => {
            const fromApproval = (purchaseRequest.approvals || []).find(
              (a) => a.justification && /cancelada por/i.test(a.justification)
            )?.justification;
            const fromActivity = activities.find(
              (a) =>
                a.new_value === 'cancelado' ||
                (a.description && /requisição cancelada por/i.test(a.description))
            )?.description;
            return fromApproval || fromActivity || 'Esta requisição não pode mais ser aprovada ou reprovada.';
          })()}
        </Alert>
      )}

      {/* Card do Fluxo de Trabalho - Horizontal no Topo */}
      {purchaseRequest.workflow_id && currentWorkflow && (
          <Card className="mb-3">
            <Card.Body>
              <WorkflowVisualizerHorizontal 
                workflow={currentWorkflow} 
                currentStep={purchaseRequest.current_step || undefined}
                ticketStatus={purchaseRequest.status}
                displayStepOrder={displayStepOrder}
                nextStepCount={nextStepOptions.length}
                onAdvance={() => handleAdvanceStep(selectedNextStep || undefined)}
                onGoBack={handleGoBackStep}
                onConclude={handleConclude}
                canAdvance={canAdvanceStep()}
                canGoBack={canGoBackStepFromAPI && !workflowBlockedByApprovals}
                canConclude={canConclude && !workflowBlockedByApprovals}
                showExtraAction={canReturnToRequesterReview}
                extraActionLabel="Retornar para revisão do requisitante"
                onExtraAction={handleReturnToRequesterReview}
                isExtraActionLoading={returningForRequesterReview}
                isLoading={isAdvancingStep}
                isGoingBack={isGoingBackStep}
                isConcluding={isConcluding}
                isLastStep={isLastStep}
              />
              {canAdvanceStep() && nextStepOptions.length > 1 && (
                <Form.Group className="mt-3">
                  <Form.Label>Próximo passo do fluxo</Form.Label>
                  <Form.Select
                    value={selectedNextStep}
                    onChange={(e) => setSelectedNextStep(e.target.value)}
                    disabled={isAdvancingStep || isGoingBackStep || isConcluding}
                  >
                    <option value="">Selecione um caminho</option>
                    {nextStepOptions.map((option, index) => {
                      const optionLabel = typeof option === 'string' ? option : (option.label || option.step || '');
                      const optionStep = typeof option === 'string' ? option : (option.step || option.label || '');
                      return (
                        <option key={`${optionStep}-${index}`} value={optionStep}>
                          {optionLabel}
                        </option>
                      );
                    })}
                  </Form.Select>
                </Form.Group>
              )}
              {!canAdvanceStep() && missingRequiredFields.length > 0 && (
                <Alert variant="warning" className="mt-3 mb-0">
                  <div className="fw-semibold">Preencha os campos obrigatórios da etapa atual para avançar:</div>
                  <ul className="mb-0 mt-2">
                    {missingRequiredFields.map((fieldName, index) => (
                      <li key={`${fieldName}-${index}`}>{fieldName}</li>
                    ))}
                  </ul>
                </Alert>
              )}
            </Card.Body>
          </Card>
      )}

      {showOpportunityPreparationAction && (
        <Card className="mb-3 border-primary">
          <Card.Body className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div>
              <h5 className="mb-1">Preparação da Oportunidade</h5>
              <p className="mb-0 text-muted">
                Organize as oportunidades, pastas e arquivos desta fase de contratação.
              </p>
            </div>
            <Button variant="primary" onClick={openOpportunityPreparation}>
              Criar ou gerenciar oportunidades
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Área de Tempo com Animação */}
      <Card className="mb-3" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <Card.Body>
          <Row>
            <Col md={6}>
              <div className="text-center">
                <h6 className="mb-2">Tempo Total da Requisição</h6>
                <h2 className="mb-0" style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>
                  {displayTimes.isReady ? formatTimeMinutesSeconds(displayTimes.totalSeconds) : '--'}
                </h2>
                <small style={{ opacity: 0.9 }}>tempo decorrido</small>
              </div>
            </Col>
            <Col md={6}>
              <div className="text-center">
                <h6 className="mb-2">Tempo na Etapa Atual</h6>
                <h2 className="mb-0" style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>
                  {displayTimes.isReady
                    ? formatTimeMinutesSeconds(displayTimes.currentStepSeconds)
                    : '--'}
                </h2>
                <small style={{ opacity: 0.9 }}>tempo decorrido</small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col md={8}>
          <Card className="mb-3">
            <Card.Header>
              <h5>{purchaseRequest.title}</h5>
            </Card.Header>
            <Card.Body>
              {canEditPurchaseContent && isOwnerForSensitive ? (
                <div className="mb-3">
                  <Form.Group className="mb-2">
                    <Form.Label>Objeto</Form.Label>
                    <Form.Control
                      value={coreObject}
                      onChange={(e) => setCoreObject(e.target.value)}
                      placeholder="Descreva o objeto da requisição"
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Descrição dos Itens</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={coreItemsDescription}
                      onChange={(e) => setCoreItemsDescription(e.target.value)}
                      placeholder="Detalhe os itens da requisição"
                    />
                  </Form.Group>
                  <div className="d-flex justify-content-end">
                    <Button
                      size="sm"
                      onClick={handleSaveCoreFields}
                      disabled={editingCoreFields}
                    >
                      {editingCoreFields ? 'Salvando...' : 'Salvar Objeto e Itens'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mb-3">
                  <p className="mb-1"><strong>Objeto:</strong> {purchaseRequest.object || '-'}</p>
                  <p className="mb-0" style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    <strong>Descrição dos Itens:</strong> {purchaseRequest.items_description || '-'}
                  </p>
                </div>
              )}
              <p style={{ whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{purchaseRequest.description}</p>
              <div className="mt-3 d-flex gap-2 align-items-center flex-wrap">
                {canEditUrgencyAndPriority ? (
                  <div className="d-inline-flex align-items-center gap-1">
                    <small className="text-muted fw-semibold">Urgência</small>
                    <Form.Select
                      value={purchaseRequest.urgency || 'BAIXA'}
                      onChange={(e) => handleUrgencyChange(e.target.value as TicketUrgency)}
                      style={{ width: 'auto' }}
                      aria-label="Urgência"
                    >
                      {URGENCY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </div>
                ) : (
                  <Badge bg={getUrgencyColor(purchaseRequest.urgency)}>
                    Urgência: {getUrgencyLabel(purchaseRequest.urgency)}
                  </Badge>
                )}
                {(canSeeInternalPriority || canEditAsRequesterOnResubmit) && (
                  canEditUrgencyAndPriority ? (
                    <div className="d-inline-flex align-items-center gap-1">
                      <small className="text-muted fw-semibold">Prioridade</small>
                      <Form.Select
                        value={purchaseRequest.priority ?? 'p4'}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        style={{ width: 'auto' }}
                        aria-label="Prioridade interna"
                      >
                        {PRIORITY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </Form.Select>
                    </div>
                  ) : purchaseRequest.priority ? (
                    <Badge bg={getPriorityColor(purchaseRequest.priority)}>
                      Prioridade: {getPriorityLabel(purchaseRequest.priority)}
                    </Badge>
                  ) : null
                )}
                <Badge bg={getStatusColor(purchaseRequest.status)}>
                  {getStatusLabel(purchaseRequest.status)}
                </Badge>
                {purchaseRequest.current_step && (
                  <Badge bg="info" className="ms-2">
                    Passo: {purchaseRequest.current_step}
                  </Badge>
                )}
              </div>
              {isWaitingStatus(purchaseRequest.status) && purchaseRequest.waiting_reason && (
                <div className="mt-2">
                  <small className="text-muted">
                    <strong>Detalhes adicionais:</strong> {purchaseRequest.waiting_reason}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>

          {canResubmitForApproval && (
            <Alert variant="warning" className="mb-3">
              <p className="mb-2">
                {isReturnedForRequesterReviewCycle
                  ? 'Esta requisição foi retornada para ajustes. Revise as informações necessárias e reenvie para uma nova rodada de aprovação.'
                  : 'Esta requisição foi reprovada. Revise a justificativa abaixo, ajuste as informações necessárias e reenvie para uma nova rodada de aprovação.'}
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={handleResubmitForApproval}
                disabled={resubmittingApproval}
              >
                {resubmittingApproval ? 'Reenviando...' : 'Reenviar para aprovação'}
              </Button>
            </Alert>
          )}

          {/* Seção de Aprovações */}
          {purchaseRequest.approvals && purchaseRequest.approvals.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <h5>Aprovações</h5>
              </Card.Header>
              <Card.Body>
                {purchaseRequest.approvals && purchaseRequest.approvals.length > 0 ? (
                  <Table responsive>
                    <thead>
                      <tr>
                        <th>Aprovador</th>
                        <th>Tipo de aprovação</th>
                        <th>Status</th>
                        <th>Data</th>
                        <th>Perguntas e respostas</th>
                        <th>Justificativa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseRequest.approvals.map((approval) => {
                        // Mesma regra de antes do cancelamento: botões se a pendência for do usuário.
                        // Única adição: esconder se a requisição já foi cancelada.
                        const isCurrentUserPendingApproval =
                          !!currentUser &&
                          approval.approver_id === currentUser.id &&
                          approval.status === 'pendente' &&
                          purchaseRequest.status !== 'cancelado';
                        const answerRecords = parseApproverAnswerRecords(approval.question_answers);
                        const pendingQuestions = parseApproverQuestions(approval.approver_questions);
                        return (
                        <tr key={approval.id}>
                          <td>{approval.approver?.name || `Usuário #${approval.approver_id}`}</td>
                          <td>
                            <div>{approval.approval_type_label || 'Aprovador'}</div>
                            {approval.approval_context_label && (
                              <small className="text-muted">{approval.approval_context_label}</small>
                            )}
                          </td>
                          <td>
                            {approval.status === 'aprovado' && !isReturnedForRequesterReviewCycle && (
                              <Badge bg="success">Aprovado</Badge>
                            )}
                            {approval.status === 'aprovado' && isReturnedForRequesterReviewCycle && (
                              <Badge bg="secondary">Aguardando reenvio</Badge>
                            )}
                            {approval.status === 'reprovado' && <Badge bg="danger">Reprovado</Badge>}
                            {approval.status === 'pendente' && purchaseRequest.status !== 'cancelado' && (
                              <Badge bg="warning">Pendente</Badge>
                            )}
                            {approval.status === 'pendente' && purchaseRequest.status === 'cancelado' && (
                              <Badge bg="secondary">Cancelada</Badge>
                            )}
                            {isCurrentUserPendingApproval && (
                              <div className="mt-2 d-flex gap-2">
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => openApproveFlow(approval)}
                                  disabled={processingApproval}
                                >
                                  {processingApproval ? 'Processando...' : 'Aprovar'}
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => openRejectFlow(approval)}
                                  disabled={processingApproval}
                                >
                                  Reprovar
                                </Button>
                              </div>
                            )}
                          </td>
                          <td>
                            {approval.status !== 'pendente' && new Date(approval.updated_at).toLocaleString('pt-BR')}
                            {approval.status === 'pendente' && '-'}
                          </td>
                          <td>
                            {answerRecords.length > 0 && (
                              <ApproverQuestionAnswers records={answerRecords} />
                            )}
                            {answerRecords.length === 0 && pendingQuestions.length > 0 && (
                              <small className="text-muted">A responder</small>
                            )}
                            {answerRecords.length === 0 && pendingQuestions.length === 0 && '-'}
                          </td>
                          <td>
                            {approval.justification && (
                              <small className="text-muted">{approval.justification}</small>
                            )}
                            {!approval.justification && '-'}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">Carregando informações de aprovação...</p>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Campos Customizados */}
          {(stageAccessDenied || customFields.length > 0) && (() => {
            if (stageAccessDenied && !canEditPurchaseContent) {
              return (
                <Card className="mb-3">
                  <Card.Header>
                    {renderProcessInfoHeader()}
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="warning">
                      Você não possui permissão para visualizar este conteúdo.
                    </Alert>
                  </Card.Body>
                </Card>
              );
            }
            const canEditFields = canEditPurchaseContent;

            // Agrupar campos por group_name
            const groupedFields: { [key: string]: WorkflowCustomField[] } = {};
            const ungroupedFields: WorkflowCustomField[] = [];
            let hiddenProposalFieldCount = 0;

            customFields.forEach(field => {
              if (!showSensitiveProposalContent && isProposalRelatedField(field)) {
                hiddenProposalFieldCount += 1;
                return;
              }
              if (!isChecklistStage(purchaseRequest?.current_step) && isChecklistRelatedField(field)) {
                return;
              }
              if (field.group_name) {
                const groupName = field.group_name;
                if (!groupedFields[groupName]) {
                  groupedFields[groupName] = [];
                }
                groupedFields[groupName].push(field);
              } else {
                ungroupedFields.push(field);
              }
            });

            // Se não há agrupamentos, exibir normalmente
            const hasGroups = Object.keys(groupedFields).length > 0;
            const hasUngrouped = ungroupedFields.length > 0;
            const hasProposalTab = (groupedFields[PROPOSAL_TAB_NAME] || []).length > 0;
            const shouldShowProposalDeniedAlert =
              (proposalAccessDenied || hiddenProposalFieldCount > 0 || hasProposalTab) &&
              !showSensitiveProposalContent;

            // Se não há grupos e há apenas campos sem agrupamento, exibir sem tabs
            if (!hasGroups && hasUngrouped) {
              return (
                <Card className="mb-3">
                  <Card.Header>
                    {renderProcessInfoHeader()}
                  </Card.Header>
                  <Card.Body>
                    {shouldShowProposalDeniedAlert && (
                      <Alert variant="warning">
                        Você não possui permissão para visualizar este conteúdo.
                      </Alert>
                    )}
                    <CustomFieldsEditor
                      ticketId={purchaseRequest!.id}
                      workflowId={purchaseRequest!.workflow_id!}
                      workflowName={currentWorkflow?.name}
                      currentStep={purchaseRequest!.current_step}
                      customFields={customFields}
                      allCustomFields={customFields}
                      customFieldValues={customFieldValues as any}
                      onSave={handleCustomFieldsSaved}
                      canEdit={!!canEditFields}
                      assignedGroupId={purchaseRequest!.assigned_group_id}
                      areaType="purchases"
                      purchaseAttachments={attachments}
                    />
                  </Card.Body>
                </Card>
              );
            }

            // Se há apenas um grupo e nenhum campo sem agrupamento, exibir sem tabs
            if (hasGroups && !hasUngrouped && Object.keys(groupedFields).length === 1) {
              const singleGroupFields = Object.values(groupedFields)[0];
              return (
                <Card className="mb-3">
                  <Card.Header>
                    {renderProcessInfoHeader()}
                  </Card.Header>
                  <Card.Body>
                    {shouldShowProposalDeniedAlert && (
                      <Alert variant="warning">
                        Você não possui permissão para visualizar este conteúdo.
                      </Alert>
                    )}
                    <CustomFieldsEditor
                      ticketId={purchaseRequest!.id}
                      workflowId={purchaseRequest!.workflow_id!}
                      workflowName={currentWorkflow?.name}
                      currentStep={purchaseRequest!.current_step}
                      customFields={singleGroupFields}
                      allCustomFields={customFields}
                      customFieldValues={customFieldValues as any}
                      areaType="purchases"
                      purchaseAttachments={attachments}
                      onSave={handleCustomFieldsSaved}
                      canEdit={!!canEditFields}
                      assignedGroupId={purchaseRequest!.assigned_group_id}
                    />
                  </Card.Body>
                </Card>
              );
            }

            // Exibir com tabs
            const tabKeys = [...Object.keys(groupedFields), ...(hasUngrouped ? ['ungrouped'] : [])];
            const selectedTabKey = tabKeys.includes(activeProcessInfoTab)
              ? activeProcessInfoTab
              : (tabKeys[0] || 'ungrouped');
            const selectedTabFields =
              selectedTabKey === 'ungrouped'
                ? ungroupedFields
                : (groupedFields[selectedTabKey] || []);

            return (
              <Card className="mb-3">
                <Card.Header>
                  {renderProcessInfoHeader()}
                </Card.Header>
                <Card.Body>
                  {shouldShowProposalDeniedAlert && (
                    <Alert variant="warning">
                      Você não possui permissão para visualizar este conteúdo.
                    </Alert>
                  )}
                  <Tabs
                    activeKey={selectedTabKey}
                    onSelect={(nextKey) => setActiveProcessInfoTab(nextKey || '')}
                    className="mb-3"
                  >
                    {Object.entries(groupedFields).map(([groupName]) => (
                      <Tab key={groupName} eventKey={groupName} title={groupName} />
                    ))}
                    {hasUngrouped && (
                      <Tab eventKey="ungrouped" title="Geral" />
                    )}
                  </Tabs>
                  <CustomFieldsEditor
                    ticketId={purchaseRequest!.id}
                    workflowId={purchaseRequest!.workflow_id!}
                    workflowName={currentWorkflow?.name}
                    currentStep={purchaseRequest!.current_step}
                    customFields={selectedTabFields}
                    allCustomFields={customFields}
                    customFieldValues={customFieldValues as any}
                    areaType="purchases"
                    purchaseAttachments={attachments}
                    onSave={handleCustomFieldsSaved}
                    canEdit={!!canEditFields}
                    assignedGroupId={purchaseRequest!.assigned_group_id}
                  />
                </Card.Body>
              </Card>
            );
          })()}

          {/* Documentos de campos personalizados (plataforma, upload, file/files) */}
          {(() => {
            const linkedDocuments = buildLinkedDocumentsFromCustomFieldValues(
              customFieldValues,
              customFields,
            );

            if (linkedDocuments.length === 0) {
              return null;
            }

            return (
              <Card className="mb-3">
                <Card.Header>
                  <h5>Documentos Vinculados</h5>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {linkedDocuments.map((item, index) => {
                      const linkedDocumentId = item.document?.id;
                      return (
                      <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <div className="fw-bold">{item.field?.label || 'Documento'}</div>
                          {item.document ? (
                            <>
                              <div className="text-muted">
                                {item.document.document_type?.name || 'Documento'}
                                {item.document.title && ` - ${item.document.title}`}
                              </div>
                              <small className="text-muted">
                                Criado em: {new Date(item.document.created_at || '').toLocaleDateString('pt-BR')}
                              </small>
                            </>
                          ) : (
                            <div className="text-muted text-break">
                              Arquivo enviado: {item.fileName || 'Arquivo'}
                              {item.fileMissing && (
                                <Badge bg="warning" text="dark" className="ms-2">
                                  Indisponível
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                        {linkedDocumentId ? (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => window.open(`/documents/${linkedDocumentId}/edit`, '_blank')}
                          >
                            Abrir Documento
                          </Button>
                        ) : (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() =>
                              downloadFile(item.filePath || '', item.fileName || 'arquivo', {
                                onSessionExpired: () =>
                                  showModalAlert({
                                    title: 'Sessão expirada',
                                    message: 'Por favor, faça login novamente.',
                                    variant: 'warning',
                                  }),
                                onError: (msg) =>
                                  showModalAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                                onFileMissing: () =>
                                  showModalAlert({
                                    title: 'Arquivo indisponível',
                                    message: 'Este ficheiro não foi encontrado no armazenamento.',
                                    variant: 'warning',
                                  }),
                              })
                            }
                          >
                            Baixar Arquivo
                          </Button>
                        )}
                      </ListGroup.Item>
                    );
                    })}
                  </ListGroup>
                </Card.Body>
              </Card>
            );
          })()}

          {!parentRequestId && (
            <Card className="mb-3">
              <Card.Header className="d-flex justify-content-between">
                <span>Subtarefas {subtasks.length > 0 && `(${subtasks.length})`}</span>
                {!isFinalStatus(purchaseRequest.status) && canCreateSubtask() && (
                  <Button size="sm" variant="success" onClick={() => setShowSubtaskModal(true)}>
                    Nova Subtarefa
                  </Button>
                )}
              </Card.Header>
              {subtasks.length > 0 ? (
                <Card.Body>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Título</th>
                        <th>Status</th>
                        <th>Urgência</th>
                        {canSeeInternalPriority && <th>Prioridade</th>}
                        <th>Destino</th>
                        <th>Responsável</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subtasks.map((subtask) => {
                        const responsibleUser = subtask.taken_by_user || subtask.assignee;
                        const isIndividual =
                          subtask.is_individual_subtask ??
                          (!!subtask.assigned_to && !subtask.assigned_group_id);
                        const assignedGroupName = subtask.assigned_group_id
                          ? groups.find((group) => group.id === subtask.assigned_group_id)?.name
                          : undefined;
                        return (
                          <tr key={subtask.id}>
                            <td>#{subtask.public_id || subtask.id}</td>
                            <td>{subtask.title}</td>
                            <td>
                              <Badge bg={getStatusColor(subtask.status)}>
                                {getStatusLabel(subtask.status)}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={getUrgencyColor(subtask.urgency)}>
                                {getUrgencyLabel(subtask.urgency)}
                              </Badge>
                            </td>
                            {canSeeInternalPriority && (
                              <td>
                                {subtask.priority ? (
                                  <Badge bg={getPriorityColor(subtask.priority)}>
                                    {getPriorityLabel(subtask.priority)}
                                  </Badge>
                                ) : (
                                  '—'
                                )}
                              </td>
                            )}
                            <td>
                              {isIndividual ? (
                                <Badge bg="info">Usuário</Badge>
                              ) : (
                                <Badge bg="secondary">
                                  {assignedGroupName ? `Grupo: ${assignedGroupName}` : 'Grupo'}
                                </Badge>
                              )}
                            </td>
                            <td>
                              {responsibleUser ? responsibleUser.name : 'Não atribuído'}
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => navigate(`/purchases/${subtask.id}`)}
                              >
                                Ver
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card.Body>
              ) : (
                <Card.Body>
                  <p className="text-muted mb-0">Nenhuma subtarefa criada ainda.</p>
                </Card.Body>
              )}
            </Card>
          )}

          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between">
              <span>Comentários</span>
              {!isFinalStatus(purchaseRequest.status) && (
                <Button size="sm" variant="success" onClick={() => setShowCommentModal(true)}>
                  Adicionar Comentário
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {comments.length === 0 ? (
                <p className="text-muted">Nenhum comentário</p>
              ) : (
                <ListGroup variant="flush">
                  {comments.map((comment) => (
                    <ListGroup.Item key={comment.id}>
                      <div className="d-flex justify-content-between">
                        <div>
                          <strong>{comment.user?.name || 'Usuário'}</strong>
                          {comment.is_internal && (
                            <Badge bg="warning" className="ms-2">Interno</Badge>
                          )}
                        </div>
                        <small className="text-muted">
                          {new Date(comment.created_at).toLocaleString('pt-BR')}
                        </small>
                      </div>
                      <div 
                        className="mt-2 mb-0 comment-content csp-wave1-comment-content" 
                        dangerouslySetInnerHTML={{ __html: sanitizeHTML(comment.content) }}
                      />
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>

          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between">
              <span>Log de Atividades</span>
            </Card.Header>
            <Card.Body>
              {activities.length === 0 ? (
                <p className="text-muted">Nenhuma atividade registrada</p>
              ) : (
                <ListGroup variant="flush">
                  {activities.map((activity) => (
                    <ListGroup.Item key={activity.id}>
                      <div className="d-flex justify-content-between mb-1">
                        <div>
                          <Badge bg={getActivityColor(activity.activity_type)} className="me-2">
                            {activity.activity_type}
                          </Badge>
                          <strong>{activity.user?.name || 'Usuário'}</strong>
                        </div>
                        <small className="text-muted">
                          {new Date(activity.created_at).toLocaleString('pt-BR')}
                        </small>
                      </div>
                      <p className="mb-0">{activity.description}</p>
                      {(activity.old_value || activity.new_value) && (
                        <div className="mt-2 small">
                          <div className="text-muted fw-semibold">Alteração de valor</div>
                          <div className="mt-1">
                            <span className="text-muted fw-semibold">Antes: </span>
                            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {formatActivityValue(activity.old_value, supplierNamesById)}
                            </span>
                          </div>
                          <div className="mt-1">
                            <span className="text-muted fw-semibold">Depois: </span>
                            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {formatActivityValue(activity.new_value, supplierNamesById)}
                            </span>
                          </div>
                        </div>
                      )}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="detail-page-sidebar">
          <Card className="mb-3">
            <Card.Header>Informações</Card.Header>
            <Card.Body>
              <p><strong>Criado por:</strong> {purchaseRequest.creator?.name || 'N/A'}</p>
              {isIndividualSubtask ? (
                <p>
                  <strong>Destino:</strong>{' '}
                  <Badge bg="info">Subtarefa individual</Badge>
                  <span className="ms-2">
                    {purchaseRequest.assignee?.name || `Usuário #${purchaseRequest.assigned_to}`}
                  </span>
                </p>
              ) : (
                <p><strong>Grupo atribuído:</strong> {
                  purchaseRequest.assigned_group_id
                    ? groups.find(g => g.id === purchaseRequest.assigned_group_id)?.name || `Grupo #${purchaseRequest.assigned_group_id}`
                    : 'Não atribuído'
                }</p>
              )}
              <p><strong>Analista atribuído:</strong> {purchaseRequest.assignee?.name || 'Não atribuído'}</p>
              {purchaseRequest.workflow_id && (
                <p><strong>Fluxo:</strong> {currentWorkflow?.name || 'N/A'}</p>
              )}
              {parentRequestId && (
                <p><strong>Requisição Pai:</strong>
                  {canOpenParentRequest ? (
                    <Button
                      variant="link"
                      className="p-0 ms-2"
                      onClick={() => navigate(`/purchases/${parentRequestId}`)}
                    >
                      Ver Requisição #{purchaseRequest.parent_public_id || parentRequestId}
                    </Button>
                  ) : (
                    <span className="ms-2 text-muted">
                      #{purchaseRequest.parent_public_id || parentRequestId} (sem acesso)
                    </span>
                  )}
                </p>
              )}
              <p><strong>Data de criação:</strong> {new Date(purchaseRequest.created_at).toLocaleString('pt-BR')}</p>
              <p><strong>Última atualização:</strong> {new Date(purchaseRequest.updated_at).toLocaleString('pt-BR')}</p>
              {purchaseRequest.taken_by_user && (
                <p><strong>Assumido por:</strong> {purchaseRequest.taken_by_user.name}</p>
              )}
              <div className="sn-action-buttons mt-2">
                {canTakePurchaseRequest() && !isFinalStatus(purchaseRequest.status) && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleTakePurchaseRequest}
                  >
                    Assumir Requisição
                  </Button>
                )}
                {canAssign() && !isFinalStatus(purchaseRequest.status) && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowAssignModal(true)}
                  >
                    Distribuir Requisição
                  </Button>
                )}
                {canShowFinalReportButton && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={isExportingFinalReport}
                    onClick={() => openFinalReportModal(false)}
                    title="Configurar e gerar relatório final"
                  >
                    Gerar Relatório Final
                  </Button>
                )}
                {canShowFinalReportWithCalculationsButton && (
                  <Button
                    variant="outline-primary"
                    className="btn-outline-orange"
                    size="sm"
                    disabled={isExportingFinalReport}
                    onClick={() => openFinalReportModal(true)}
                    title="Gerar relatório final com cálculos nas tabelas de proposta"
                  >
                    Relatório com Cálculos
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Documentos da Oportunidade</span>
              {!isFinalStatus(purchaseRequest.status) && !attachmentAccessDenied && canUploadPurchaseAttachment && (
                <Button size="sm" variant="success" onClick={() => setShowAttachmentModal(true)}>
                  + Adicionar Anexo
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {attachmentAccessDenied && (
                <Alert variant="warning">
                  Você não possui permissão para visualizar este conteúdo.
                </Alert>
              )}
              {(() => {
                if (attachmentAccessDenied) {
                  return <p className="text-muted">Nenhum anexo</p>;
                }
                const opportunityAreaAttachments = attachments.filter(
                  (attachment) => !isCustomFieldOriginAttachment(attachment)
                );
                // Liberação explícita feita por analista/admin prevalece sobre os filtros
                // heurísticos: o anexo marcado como visível deve aparecer para o requisitante.
                const isReleasedToRequester = (attachment: PurchaseAttachment) =>
                  isOwnerForSensitive && !!attachment.visible_to_requester;
                const hasProposalLikeAttachments = opportunityAreaAttachments.some((attachment) =>
                  isProposalLikeAttachment(attachment.filename, attachment.description)
                );
                const visibleAttachments = showSensitiveProposalContent
                  ? opportunityAreaAttachments
                  : opportunityAreaAttachments.filter(
                      (attachment) =>
                        !isProposalLikeAttachment(attachment.filename, attachment.description) ||
                        isReleasedToRequester(attachment)
                    );
                const hasRestrictedAttachments = hasProposalLikeAttachments && !showSensitiveProposalContent;
                if (hasRestrictedAttachments) {
                  return (
                    <>
                      <Alert variant="info">
                        Alguns anexos não estão disponíveis para o seu perfil nesta etapa.
                      </Alert>
                      {visibleAttachments.length === 0 ? (
                        <p className="text-muted">Nenhum anexo liberado para visualização.</p>
                      ) : (
                        <ListGroup variant="flush">
                          {visibleAttachments.map((attachment) => {
                            return (
                            <ListGroup.Item key={`${attachment.id}-${attachment.file_path}`}>
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      downloadFile(attachment.file_path, attachment.filename, {
                                        onSessionExpired: () => showModalAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                                        onError: (msg) => showModalAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                                        onFileMissing: () =>
                                          showModalAlert({
                                            title: 'Arquivo indisponível',
                                            message: 'Este anexo não foi encontrado no armazenamento.',
                                            variant: 'warning',
                                          }),
                                      });
                                    }}
                                    className="btn btn-link p-0 text-decoration-none align-baseline"
                                  >
                                    <strong>{attachment.filename}</strong>
                                  </button>
                                  {attachment.file_missing && (
                                    <Badge bg="warning" text="dark" className="ms-2">
                                      Indisponível
                                    </Badge>
                                  )}
                                  {attachment.description && (
                                    <p className="text-muted mb-1 mt-1">{attachment.description}</p>
                                  )}
                                  {canManageAttachmentVisibility && (
                                    <div className="mb-1">
                                      <Badge bg={attachment.visible_to_requester ? 'success' : 'secondary'}>
                                        {attachment.visible_to_requester
                                          ? 'Requisitante pode ver/baixar'
                                          : 'Somente analista/admin'}
                                      </Badge>
                                      {attachment.id > 0 && (
                                        <Button
                                          size="sm"
                                          variant="link"
                                          className="p-0 ms-2 align-baseline"
                                          disabled={updatingAttachmentVisibilityId === attachment.id}
                                          onClick={() =>
                                            handleToggleAttachmentRequesterVisibility(
                                              attachment,
                                              !attachment.visible_to_requester
                                            )
                                          }
                                        >
                                          {updatingAttachmentVisibilityId === attachment.id
                                            ? 'Atualizando...'
                                            : attachment.visible_to_requester
                                              ? 'Ocultar'
                                              : 'Permitir'}
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                  {canEditPurchaseContent &&
                                    isOwnerForSensitive &&
                                    attachment.id > 0 &&
                                    attachment.uploaded_by === currentUser?.id && (
                                      <div className="mb-1">
                                        <Button
                                          size="sm"
                                          variant="outline-danger"
                                          onClick={() => handleDeleteAttachment(attachment)}
                                          disabled={deletingAttachmentId === attachment.id}
                                        >
                                          {deletingAttachmentId === attachment.id ? 'Excluindo...' : 'Excluir anexo'}
                                        </Button>
                                      </div>
                                    )}
                                  <small className="text-muted">
                                    {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(2)} KB` : ''}
                                    {' • '}
                                    {new Date(attachment.uploaded_at).toLocaleString('pt-BR')}
                                  </small>
                                </div>
                              </div>
                            </ListGroup.Item>
                          );
                          })}
                        </ListGroup>
                      )}
                    </>
                  );
                }
                if (visibleAttachments.length === 0) {
                  return <p className="text-muted">Nenhum anexo liberado para visualização.</p>;
                }
                return (
                <ListGroup variant="flush">
                  {visibleAttachments.map((attachment) => {
                    return (
                    <ListGroup.Item key={`${attachment.id}-${attachment.file_path}`}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <button
                            type="button"
                            onClick={() => {
                              downloadFile(attachment.file_path, attachment.filename, {
                                onSessionExpired: () => showModalAlert({ title: 'Sessão expirada', message: 'Por favor, faça login novamente.', variant: 'warning' }),
                                onError: (msg) => showModalAlert({ title: 'Erro no download', message: msg, variant: 'danger' }),
                                onFileMissing: () =>
                                  showModalAlert({
                                    title: 'Arquivo indisponível',
                                    message: 'Este anexo não foi encontrado no armazenamento.',
                                    variant: 'warning',
                                  }),
                              });
                            }}
                            className="btn btn-link p-0 text-decoration-none align-baseline"
                          >
                            <strong>{attachment.filename}</strong>
                          </button>
                          {attachment.file_missing && (
                            <Badge bg="warning" text="dark" className="ms-2">
                              Indisponível
                            </Badge>
                          )}
                          {attachment.description && (
                            <p className="text-muted mb-1 mt-1">{attachment.description}</p>
                          )}
                          {canManageAttachmentVisibility && (
                            <div className="mb-1">
                              <Badge bg={attachment.visible_to_requester ? 'success' : 'secondary'}>
                                {attachment.visible_to_requester
                                  ? 'Requisitante pode ver/baixar'
                                  : 'Somente analista/admin'}
                              </Badge>
                              {attachment.id > 0 && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  className="p-0 ms-2 align-baseline"
                                  disabled={updatingAttachmentVisibilityId === attachment.id}
                                  onClick={() =>
                                    handleToggleAttachmentRequesterVisibility(
                                      attachment,
                                      !attachment.visible_to_requester
                                    )
                                  }
                                >
                                  {updatingAttachmentVisibilityId === attachment.id
                                    ? 'Atualizando...'
                                    : attachment.visible_to_requester
                                      ? 'Ocultar'
                                      : 'Permitir'}
                                </Button>
                              )}
                            </div>
                          )}
                          {canEditPurchaseContent &&
                            isOwnerForSensitive &&
                            attachment.id > 0 &&
                            attachment.uploaded_by === currentUser?.id && (
                              <div className="mb-1">
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteAttachment(attachment)}
                                  disabled={deletingAttachmentId === attachment.id}
                                >
                                  {deletingAttachmentId === attachment.id ? 'Excluindo...' : 'Excluir anexo'}
                                </Button>
                              </div>
                            )}
                          <small className="text-muted">
                            {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(2)} KB` : ''}
                            {' • '}
                            {new Date(attachment.uploaded_at).toLocaleString('pt-BR')}
                          </small>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                  })}
                </ListGroup>
                );
              })()}
            </Card.Body>
          </Card>

          {/* Card de Tempos por Grupo */}
          {purchaseRequestTimes && purchaseRequestTimes.time_in_groups && purchaseRequestTimes.time_in_groups.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <span>Tempos por Grupo</span>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Tempo</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseRequestTimes.time_in_groups.map((groupTime, index) => {
                      // Requisição final ou pausada usa apenas o valor do backend, sem animação.
                      const purchaseIsFinal = isFinalStatus(purchaseRequest?.status || '');
                      const displaySeconds = (groupTime.is_active && purchaseRequestTimes.current_group_time && !purchaseIsFinal)
                        ? purchaseRequestTimes.current_group_time + liveOverlaySeconds
                        : groupTime.time_spent_seconds;
                      return (
                        <tr key={index}>
                          <td><strong>{groupTime.group_name}</strong></td>
                          <td>
                            <small>
                              {new Date(groupTime.assigned_at).toLocaleString('pt-BR')}
                            </small>
                          </td>
                          <td>
                            <small>
                              {(purchaseIsFinal && groupTime.unassigned_at)
                                ? new Date(groupTime.unassigned_at).toLocaleString('pt-BR')
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <strong>{formatTimeMinutesSeconds(displaySeconds)}</strong>
                          </td>
                          <td>
                            {groupTime.is_active ? (
                              <Badge bg="success">Ativo</Badge>
                            ) : (
                              <Badge bg="secondary">{purchaseIsFinal ? 'Finalizado' : 'Pausado'}</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Linha de Total */}
                    <tr className="table-info">
                      <td colSpan={3}><strong>TOTAL</strong></td>
                      <td>
                        <strong>{formatTimeMinutesSeconds(purchaseRequestTimes.total_time_seconds)}</strong>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Card de Tempos por Etapa do Fluxo */}
          {purchaseRequestTimes && purchaseRequestTimes.time_in_steps && purchaseRequestTimes.time_in_steps.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <span>Tempos por Etapa do Fluxo</span>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Etapa</th>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Tempo</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseRequestTimes.time_in_steps.map((stepTime, index) => {
                      const purchaseIsFinal = isFinalStatus(purchaseRequest?.status || '');
                      const purchaseIsPausedByWorkflow =
                        purchaseRequest?.status === 'aguardando_aprovacao' ||
                        purchaseRequest?.status === 'aguardando_reenvio';
                      const pausedStepReference =
                        purchaseRequest?.status === 'aguardando_reenvio' &&
                        purchaseRequest?.requester_review_return_step
                          ? purchaseRequest.requester_review_return_step
                          : purchaseRequest?.current_step;
                      const currentStepKey = String(pausedStepReference || '').trim().toLowerCase();
                      const stepKey = String(stepTime.step_name || '').trim().toLowerCase();
                      const isCurrentPausedStep =
                        !purchaseIsFinal &&
                        purchaseIsPausedByWorkflow &&
                        !stepTime.is_active &&
                        currentStepKey.length > 0 &&
                        stepKey === currentStepKey;
                      const displaySeconds = stepTime.time_spent_seconds;
                      return (
                        <tr key={index}>
                          <td><strong>{stepTime.step_name}</strong></td>
                          <td>
                            <small>
                              {new Date(stepTime.started_at).toLocaleString('pt-BR')}
                            </small>
                          </td>
                          <td>
                            <small>
                              {(purchaseIsFinal && stepTime.ended_at)
                                ? new Date(stepTime.ended_at).toLocaleString('pt-BR')
                                : '-'}
                            </small>
                          </td>
                          <td>
                            <strong>{formatTimeMinutesSeconds(displaySeconds)}</strong>
                          </td>
                          <td>
                            {stepTime.is_active ? (
                              <Badge bg="success">Ativo</Badge>
                            ) : isCurrentPausedStep ? (
                              <Badge bg="secondary">Pausado</Badge>
                            ) : (
                              <Badge bg="secondary">Finalizado</Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Linha de Total */}
                    <tr className="table-info">
                      <td colSpan={3}><strong>TOTAL</strong></td>
                      <td>
                        <strong>{formatTimeMinutesSeconds(
                          purchaseRequestTimes.time_in_steps.reduce((sum, step) => sum + step.time_spent_seconds, 0)
                        )}</strong>
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* Card de Tempos Aguardando */}
          {purchaseRequestTimes && purchaseRequestTimes.time_waiting && purchaseRequestTimes.time_waiting.length > 0 && (
            <Card className="mb-3">
              <Card.Header>
                <span>Tempos Aguardando</span>
              </Card.Header>
              <Card.Body>
                <Table responsive hover size="sm">
                  <thead>
                    <tr>
                      <th>Início</th>
                      <th>Fim</th>
                      <th>Duração</th>
                      <th>Grupo</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseRequestTimes.time_waiting.map((waitingTime, index) => {
                      const purchaseIsFinal = isFinalStatus(purchaseRequest?.status || '');
                      const displaySeconds = waitingTime.is_active && !purchaseIsFinal
                        ? waitingTime.time_spent_seconds + liveOverlaySeconds
                        : waitingTime.time_spent_seconds;
                      return (
                        <tr key={index}>
                          <td>
                            {new Date(waitingTime.started_at).toLocaleString('pt-BR')}
                          </td>
                          <td>
                            {(purchaseIsFinal && waitingTime.ended_at)
                              ? new Date(waitingTime.ended_at).toLocaleString('pt-BR')
                              : <Badge bg="info">Aguardando</Badge>
                            }
                          </td>
                          <td><strong>{formatTimeMinutesSeconds(displaySeconds)}</strong></td>
                          <td>{waitingTime.group_name || 'N/A'}</td>
                          <td>
                            <small className="text-muted">
                              {waitingTime.waiting_reason || 'Não informado'}
                            </small>
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan={2}><strong>Total Aguardando</strong></td>
                      <td>
                        <strong>{formatTimeMinutesSeconds(
                          purchaseRequestTimes.total_waiting_time_seconds || 
                          purchaseRequestTimes.time_waiting.reduce((sum, wt) => sum + (wt.is_active && !isFinalStatus(purchaseRequest?.status || '') ? wt.time_spent_seconds + liveOverlaySeconds : wt.time_spent_seconds), 0)
                        )}</strong>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>

      <CommentModal
        show={showCommentModal}
        onHide={() => {
          if (isAddingComment) return;
          setShowCommentModal(false);
          setCommentText('');
          setIsInternal(false);
        }}
        commentText={commentText}
        setCommentText={setCommentText}
        isInternal={isInternal}
        setIsInternal={setIsInternal}
        onSubmit={handleAddComment}
        canCreateInternal={userCanManageOpportunities(currentUser?.role)}
        isSubmitting={isAddingComment}
      />

      {canAssign() && (
        <AssignModal
          show={showAssignModal}
          onHide={() => setShowAssignModal(false)}
          users={users}
          groups={groups}
          workflowId={purchaseRequest.workflow_id}
          currentStep={purchaseRequest.current_step}
          isSubtask={!!purchaseRequest.parent_request_id}
          currentAssignedTo={purchaseRequest.assigned_to}
          currentAssignedGroup={purchaseRequest.assigned_group_id}
          onAssign={handleAssign}
        />
      )}

      <PurchaseSubtaskModal
        show={showSubtaskModal}
        onHide={() => setShowSubtaskModal(false)}
        onSubmit={handleCreateSubtask}
        workflowId={purchaseRequest?.workflow_id}
        isLoading={isCreatingSubtask}
        canSetPriority={canManagePurchasePriority(currentUser)}
        priorityOptions={PRIORITY_OPTIONS}
      />

      {/* Modal de Motivo do Aguardando */}
      <Modal show={showWaitingReasonModal} onHide={() => {
        setShowWaitingReasonModal(false);
        setWaitingReason('');
        setPendingStatus('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Informar Detalhes Adicionais</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Detalhes Adicionais (Opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={waitingReason}
              onChange={(e) => setWaitingReason(e.target.value)}
              placeholder="Adicione informações adicionais sobre o motivo do aguardando (opcional)..."
            />
            <Form.Text className="text-muted">
              O status já indica o tipo de aguardando. Você pode adicionar detalhes adicionais aqui se necessário.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowWaitingReasonModal(false);
            setWaitingReason('');
            setPendingStatus('');
          }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleConfirmWaitingStatus}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Alerta */}
      <AlertModal
        show={showAlertModal}
        onHide={() => setShowAlertModal(false)}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        details={alertModal.details}
      />

      <Modal
        show={showChangeWorkflowModal}
        onHide={() => {
          setShowChangeWorkflowModal(false);
          setTargetWorkflowId('');
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Alterar tipo de contratação</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="py-2">
            Ao trocar o fluxo, campos exclusivos do tipo atual podem ser descartados.
            Campos equivalentes serão preservados quando possível.
          </Alert>
          <Form.Group className="mb-0">
            <Form.Label>Novo tipo de contratação</Form.Label>
            <Form.Select
              value={targetWorkflowId}
              onChange={(e) => setTargetWorkflowId(e.target.value)}
            >
              <option value="">Selecione um fluxo</option>
              {purchaseWorkflowOptions.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowChangeWorkflowModal(false);
              setTargetWorkflowId('');
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmWorkflowChange}
            disabled={!targetWorkflowId || isChangingWorkflow}
          >
            {isChangingWorkflow ? 'Alterando...' : 'Confirmar alteração'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Anexo */}
      <Modal show={showAttachmentModal} onHide={() => {
        setShowAttachmentModal(false);
        setAttachmentFile(null);
        setAttachmentDescription('');
        setAttachmentVisibleToRequester(false);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Adicionar Anexo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Arquivo</Form.Label>
            <input
              type="file"
              className="form-control"
              accept={ATTACHMENT_ACCEPT}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const error = validateAttachmentFile(file);
                if (error) {
                  showAlert('Arquivo inválido', error, 'warning');
                  e.target.value = '';
                  setAttachmentFile(null);
                  return;
                }
                setAttachmentFile(file);
              }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Descrição do Anexo</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Descreva o que se trata este anexo..."
              value={attachmentDescription}
              onChange={(e) => setAttachmentDescription(e.target.value)}
            />
          </Form.Group>
          {canManageAttachmentVisibility && (
            <Form.Group className="mb-2">
              <Form.Check
                id="attachment-visible-to-requester"
                type="checkbox"
                checked={attachmentVisibleToRequester}
                onChange={(e) => setAttachmentVisibleToRequester(e.target.checked)}
                label="Permitir que o usuário requisitante visualize e baixe este anexo"
              />
              <Form.Text className="text-muted">
                Ative esta opção apenas para arquivos que o requisitante pode consultar.
                Quando desativada, somente analistas e administradores verão o anexo.
              </Form.Text>
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowAttachmentModal(false);
            setAttachmentFile(null);
            setAttachmentDescription('');
            setAttachmentVisibleToRequester(false);
          }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleFileUpload}>
            Adicionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Aprovação com perguntas adicionais */}
      <Modal
        show={showApproveModal}
        onHide={() => {
          setShowApproveModal(false);
          setApprovalInAction(null);
          setApproverAnswers({});
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Aprovar Requisição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {purchaseRequest && (
            <>
              <p>
                <strong>Requisição #{purchasePublicId}:</strong> {purchaseRequest.title}
              </p>
              <p className="text-muted">
                Responda às perguntas abaixo para concluir a aprovação. As respostas ficam
                registradas junto da sua aprovação.
              </p>
              <ApproverQuestionsForm
                questions={parseApproverQuestions(approvalInAction?.approver_questions)}
                answers={approverAnswers}
                onChange={setApproverAnswers}
                disabled={processingApproval}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowApproveModal(false);
              setApprovalInAction(null);
              setApproverAnswers({});
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="success"
            onClick={() => handleApprove(approvalInAction || undefined)}
            disabled={processingApproval}
          >
            {processingApproval ? 'Aprovando...' : 'Confirmar Aprovação'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Reprovação */}
      <Modal
        show={showRejectModal}
        onHide={() => {
          setShowRejectModal(false);
          setApprovalInAction(null);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Reprovar Requisição</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {purchaseRequest && (
            <>
              <p>
                <strong>Requisição #{purchasePublicId}:</strong> {purchaseRequest.title}
              </p>
              <p className="text-muted">
                Por favor, informe a justificativa para a reprovação desta requisição. 
                O requisitante será notificado e poderá fazer as modificações necessárias.
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Justificativa <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={rejectionJustification}
                  onChange={(e) => setRejectionJustification(e.target.value)}
                  placeholder="Descreva o motivo da reprovação..."
                  required
                />
              </Form.Group>
              <ApproverQuestionsForm
                questions={parseApproverQuestions(approvalInAction?.approver_questions)}
                answers={approverAnswers}
                onChange={setApproverAnswers}
                disabled={processingApproval}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRejectModal(false);
              setApprovalInAction(null);
              setApproverAnswers({});
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={!rejectionJustification.trim() || processingApproval}
          >
            {processingApproval ? 'Reprovando...' : 'Confirmar Reprovação'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showFinalReportModal} onHide={closeFinalReportModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {finalReportWithProposalCalculations
              ? 'Gerar Relatório Final com Cálculos'
              : 'Gerar Relatório Final'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingFinalReportOptions ? (
            <p className="text-muted mb-0">Carregando campos customizáveis...</p>
          ) : (
            <>
              {finalReportWithProposalCalculations && (
                <Alert variant="primary" className="py-2">
                  Este relatório incluirá cálculos nas tabelas de proposta (área Compras e Contratações).
                </Alert>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Formato de exportação</Form.Label>
                <div className="d-flex gap-3">
                  <Form.Check
                    type="radio"
                    id="final-report-format-xlsx"
                    name="final-report-format"
                    label="Excel (.xlsx)"
                    checked={selectedFinalReportFormat === 'xlsx'}
                    onChange={() => setSelectedFinalReportFormat('xlsx')}
                  />
                  {!finalReportWithProposalCalculations && (
                    <Form.Check
                      type="radio"
                      id="final-report-format-pdf"
                      name="final-report-format"
                      label="PDF (.pdf)"
                      checked={selectedFinalReportFormat === 'pdf'}
                      onChange={() => setSelectedFinalReportFormat('pdf')}
                    />
                  )}
                </div>
                {finalReportWithProposalCalculations && (
                  <Form.Text className="text-muted">
                    Exportação disponível apenas em Excel (.xlsx) para este relatório.
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Group>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Campos customizáveis para exportação</Form.Label>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSelectedFinalReportFieldIds(finalReportPreview?.custom_fields.map((field) => field.custom_field_id) || [])}
                    >
                      Marcar todos
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => setSelectedFinalReportFieldIds([])}
                    >
                      Limpar seleção
                    </Button>
                  </div>
                </div>

                {finalReportPreview?.custom_fields.length ? (
                  <div className="border rounded p-2" style={{ maxHeight: '320px', overflowY: 'auto' }}>
                    {finalReportPreview.custom_fields.map((field) => (
                      <Form.Check
                        key={field.custom_field_id}
                        type="checkbox"
                        id={`final-report-field-${field.custom_field_id}`}
                        className="mb-2"
                        label={`${field.label} (${field.field_type})`}
                        checked={selectedFinalReportFieldIds.includes(field.custom_field_id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedFinalReportFieldIds((prev) =>
                              prev.includes(field.custom_field_id) ? prev : [...prev, field.custom_field_id]
                            );
                          } else {
                            setSelectedFinalReportFieldIds((prev) => prev.filter((id) => id !== field.custom_field_id));
                          }
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-0">Nenhum campo customizável encontrado para este fluxo.</p>
                )}
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeFinalReportModal}>
            Cancelar
          </Button>
          <Button
            variant="success"
            disabled={isLoadingFinalReportOptions || isExportingFinalReport}
            onClick={handleExportFinalReport}
          >
            {isExportingFinalReport
              ? 'Exportando...'
              : finalReportWithProposalCalculations
                ? 'Exportar em XLSX'
                : `Exportar em ${selectedFinalReportFormat.toUpperCase()}`}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const CommentModal: React.FC<{
  show: boolean;
  onHide: () => void;
  commentText: string;
  setCommentText: (text: string) => void;
  isInternal: boolean;
  setIsInternal: (internal: boolean) => void;
  onSubmit: (content: string, isInternal: boolean) => void | Promise<void>;
  canCreateInternal: boolean;
  isSubmitting?: boolean;
}> = ({ show, onHide, commentText, setCommentText, isInternal, setIsInternal, onSubmit, canCreateInternal, isSubmitting = false }) => {
  const [localSubmitting, setLocalSubmitting] = React.useState(false);
  const submitting = isSubmitting || localSubmitting;
  const editorRef = React.useRef<HTMLDivElement>(null);
  const [selectedColor, setSelectedColor] = React.useState('#000000');
  const [formatState, setFormatState] = React.useState({ bold: false, italic: false, underline: false });

  const syncFormatState = React.useCallback(() => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const anchor = selection.anchorNode;
    if (anchor && !editorRef.current.contains(anchor)) return;
    try {
      setFormatState({
        bold: !!document.queryCommandState('bold'),
        italic: !!document.queryCommandState('italic'),
        underline: !!document.queryCommandState('underline'),
      });
    } catch {
      // ignore unsupported browser commands
    }
  }, []);

  React.useEffect(() => {
    if (show && editorRef.current) {
      // Inicializa o editor apenas ao abrir o modal.
      // Evita reescrever HTML a cada tecla, o que desloca o cursor e inverte digitação.
      editorRef.current.innerHTML = stripBidiControlChars(commentText || '');
      setTimeout(syncFormatState, 0);
    } else if (!show && editorRef.current) {
      // Limpar ao fechar
      editorRef.current.innerHTML = '';
      setFormatState({ bold: false, italic: false, underline: false });
    }
  }, [show, commentText, syncFormatState]);

  React.useEffect(() => {
    if (!show) return;
    const handler = () => syncFormatState();
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, [show, syncFormatState]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    syncFormatState();
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    if (editorRef.current) {
      applyColorClassToSelection(editorRef.current, color);
      syncFormatState();
      editorRef.current.focus();
    }
  };

  const handleEditorInput = () => {
    syncFormatState();
  };

  const handleEditorBlur = () => {
    if (!editorRef.current) return;
    setCommentText(stripBidiControlChars(editorRef.current.innerHTML));
    syncFormatState();
  };

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    const key = e.key.toLowerCase();
    if (key === 'b') {
      e.preventDefault();
      handleFormat('bold');
    } else if (key === 'i') {
      e.preventDefault();
      handleFormat('italic');
    } else if (key === 'u') {
      e.preventDefault();
      handleFormat('underline');
    }
  };

  const handleClearFormat = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || editorRef.current.textContent || '';
      const normalized = stripBidiControlChars(text);
      editorRef.current.innerHTML = normalized;
      setCommentText(editorRef.current.innerHTML);
      setFormatState({ bold: false, italic: false, underline: false });
    }
  };

  const colors = [
    '#FFFFFF', '#000000', '#333333', '#666666', '#999999',
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00',
    '#FF00FF', '#00FFFF', '#FFA500', '#800080',
    '#FFC0CB', '#A52A2A', '#008000', '#000080'
  ];
  const colorSwatchClassMap: Record<string, string> = {
    '#FFFFFF': 'csp-wave1-color-swatch-white',
    '#000000': 'csp-wave1-swatch-black',
    '#333333': 'csp-wave1-swatch-gray-800',
    '#666666': 'csp-wave1-swatch-gray-600',
    '#999999': 'csp-wave1-swatch-gray-500',
    '#FF0000': 'csp-wave1-swatch-red',
    '#00FF00': 'csp-wave1-swatch-green',
    '#0000FF': 'csp-wave1-swatch-blue',
    '#FFFF00': 'csp-wave1-swatch-yellow',
    '#FF00FF': 'csp-wave1-swatch-magenta',
    '#00FFFF': 'csp-wave1-swatch-cyan',
    '#FFA500': 'csp-wave1-swatch-orange',
    '#800080': 'csp-wave1-swatch-purple',
    '#FFC0CB': 'csp-wave1-swatch-pink',
    '#A52A2A': 'csp-wave1-swatch-brown',
    '#008000': 'csp-wave1-swatch-dark-green',
    '#000080': 'csp-wave1-swatch-dark-blue',
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const content = stripBidiControlChars(editorRef.current?.innerHTML || '');
    setCommentText(content);
    if (!content.replace(/<[^>]*>/g, '').trim()) {
      return;
    }
    setLocalSubmitting(true);
    try {
      await onSubmit(enrichCommentLinks(content), isInternal);
    } finally {
      setLocalSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop={submitting ? 'static' : true} keyboard={!submitting}>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>Adicionar Comentário</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Comentário</Form.Label>
          
          {/* Barra de ferramentas */}
          <div className="border rounded-top p-2 d-flex align-items-center gap-2 flex-wrap csp-wave1-toolbar csp-wave1-toolbar-theme">
            <Button
              variant={formatState.bold ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormat('bold')}
              title="Negrito (Ctrl+B)"
              aria-pressed={formatState.bold}
            >
              <strong>B</strong>
            </Button>
            <Button
              variant={formatState.italic ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormat('italic')}
              title="Itálico (Ctrl+I)"
              aria-pressed={formatState.italic}
            >
              <em>I</em>
            </Button>
            <Button
              variant={formatState.underline ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={() => handleFormat('underline')}
              title="Sublinhado (Ctrl+U)"
              aria-pressed={formatState.underline}
            >
              <u>U</u>
            </Button>
            
            <div className="vr csp-wave1-separator" />
            
            <div className="d-flex align-items-center gap-1">
              <Form.Label className="mb-0 small">Cor:</Form.Label>
              <Form.Control
                type="color"
                value={selectedColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="csp-wave1-color-picker"
                title="Selecionar cor do texto"
              />
            </div>
            
            <div className="d-flex gap-1">
              {colors.slice(0, 8).map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`btn btn-sm border csp-wave1-color-swatch ${colorSwatchClassMap[color] || ''}`}
                  onClick={() => handleColorChange(color)}
                  title={`Cor ${color}`}
                />
              ))}
            </div>
            
            <div className="vr csp-wave1-separator" />

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                const url = window.prompt('Cole a URL do link');
                if (!url) return;
                const normalized = url.trim().startsWith('www.') ? `https://${url.trim()}` : url.trim();
                if (!/^https?:\/\//i.test(normalized) && !/^mailto:/i.test(normalized)) {
                  return;
                }
                handleFormat('createLink', normalized);
              }}
              title="Inserir link"
            >
              Link
            </Button>
            
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleClearFormat}
              title="Limpar formatação"
            >
              Limpar
            </Button>
          </div>
          
          {/* Editor de texto rico */}
          <div
            ref={editorRef}
            dir="ltr"
            contentEditable={!submitting}
            onKeyDown={handleEditorKeyDown}
            onInput={handleEditorInput}
            onBlur={handleEditorBlur}
            onPaste={(e) => {
              // Permitir colar apenas texto (remover formatação de outras fontes)
              e.preventDefault();
              const text = stripBidiControlChars(e.clipboardData.getData('text/plain'));
              document.execCommand('insertText', false, text);
              handleEditorInput();
            }}
            className="border rounded-bottom p-2 csp-wave1-editor csp-wave1-editor-theme"
            suppressContentEditableWarning={true}
          />
          
          <Form.Text className="text-muted">
            Use os botões acima para formatar o texto. Suporte a negrito, itálico, sublinhado e cores.
          </Form.Text>
        </Form.Group>
        {canCreateInternal && (
          <Form.Check
            type="checkbox"
            label="Comentário interno"
            checked={isInternal}
            onChange={(e) => setIsInternal(e.target.checked)}
            disabled={submitting}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancelar
        </Button>
        <Button 
          variant="success" 
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Adicionando...' : 'Adicionar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AssignModal: React.FC<{
  show: boolean;
  onHide: () => void;
  users: User[];
  groups: Group[];
  workflowId?: number;
  currentStep?: string;
  isSubtask?: boolean;
  currentAssignedTo?: number;
  currentAssignedGroup?: number;
  onAssign: (assignedTo?: number, assignedGroupId?: number) => void;
}> = ({
  show,
  onHide,
  users,
  groups,
  workflowId,
  currentStep,
  isSubtask = false,
  currentAssignedTo,
  currentAssignedGroup,
  onAssign,
}) => {
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [availableGroups, setAvailableGroups] = useState<Group[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);

  // Carregar grupos e usuários baseado no workflow do PurchaseRequest
  React.useEffect(() => {
    const stageGroupMarkers = isSubtask ? allowedGroupMarkersForStep(currentStep) : [];

    const filterGroupsByStep = (sourceGroups: Group[]) => {
      if (stageGroupMarkers.length === 0) return sourceGroups;
      return sourceGroups.filter((group) => {
        const normalizedGroupName = normalizeProposalToken(group.name);
        return stageGroupMarkers.some((marker) => normalizedGroupName.includes(marker));
      });
    };

    const filterUsersByStep = (sourceUsers: User[], sourceGroups: Group[]) => {
      if (stageGroupMarkers.length === 0) return sourceUsers;
      const allowedUserIds = new Set<number>();
      sourceGroups.forEach((group) => {
        const normalizedGroupName = normalizeProposalToken(group.name);
        const isAllowedGroup = stageGroupMarkers.some((marker) => normalizedGroupName.includes(marker));
        if (!isAllowedGroup || !group.members || group.members.length === 0) {
          return;
        }
        group.members.forEach((member) => allowedUserIds.add(member.user_id));
      });

      // Fallback seguro: se a API não trouxe membros do grupo,
      // não bloqueia visualmente usuários válidos; backend mantém validação final.
      if (allowedUserIds.size === 0) {
        return sourceUsers;
      }

      return sourceUsers.filter((user) => allowedUserIds.has(user.id));
    };

    const loadGroupsAndUsersForWorkflow = async () => {
      if (workflowId) {
        try {
          // Carregar grupos do workflow
          const groupsResponse = await axios.get(`${API_URL}/api/groups`, {
            params: { workflow_id: workflowId, include_members: true },
          });
          const workflowGroups = (groupsResponse.data || []).filter((g: Group) => g.is_active);
          const filteredGroups = filterGroupsByStep(workflowGroups);
          setAvailableGroups(filteredGroups);
          
          // Carregar usuários dos grupos do workflow
          const usersResponse = await axios.get(`${API_URL}/api/workflows/${workflowId}/users`);
          // O endpoint /workflows/{id}/users pode não retornar is_active em todos os ambientes.
          // Quando ausente, considerar o usuário como ativo para não esvaziar o select indevidamente.
          const workflowUsers = (usersResponse.data || []).filter((u: User) => u.is_active !== false);
          const stepFilteredUsers = filterUsersByStep(workflowUsers, filteredGroups);
          const allActiveUsers = users.filter((u: User) => u.is_active);
          const allActiveGroups = groups.filter((g: Group) => g.is_active);

          // Comportamento legado para requisição principal:
          // se o workflow não devolver elegíveis, não bloquear atribuição.
          if (!isSubtask && filteredGroups.length === 0 && stepFilteredUsers.length === 0) {
            setAvailableGroups(allActiveGroups);
            setAvailableUsers(allActiveUsers);
          } else {
            setAvailableUsers(stepFilteredUsers);
          }
        } catch (error) {
          console.error('Erro ao carregar grupos e usuários do workflow:', error);
          const allActiveUsers = users.filter((u: User) => u.is_active);
          const allActiveGroups = groups.filter((g: Group) => g.is_active);
          if (!isSubtask) {
            setAvailableGroups(allActiveGroups);
            setAvailableUsers(allActiveUsers);
          } else {
            setAvailableGroups([]);
            setAvailableUsers([]);
          }
        }
      } else {
        // Se não há workflow, mostrar todos os grupos e usuários ativos
        const allActiveGroups = groups.filter((g: Group) => g.is_active);
        const filteredGroups = filterGroupsByStep(allActiveGroups);
        setAvailableGroups(filteredGroups);
        setAvailableUsers(filterUsersByStep(users.filter((u: User) => u.is_active), filteredGroups));
      }
    };

    if (show) {
      loadGroupsAndUsersForWorkflow();
    }
  }, [workflowId, show, groups, users, currentStep, isSubtask]);

  const handleSubmit = () => {
    const userId = selectedUser ? parseInt(selectedUser) : undefined;
    const groupId = selectedGroup ? parseInt(selectedGroup) : undefined;
    onAssign(userId, groupId);
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Distribuir Requisição</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Atribuir a Usuário</Form.Label>
          <Form.Select
            value={selectedUser}
            onChange={(e) => {
              setSelectedUser(e.target.value);
              setSelectedGroup('');
            }}
            disabled={workflowId !== undefined && availableUsers.length === 0}
          >
            <option value="">
              {workflowId !== undefined && availableUsers.length === 0
                ? 'Nenhum usuário disponível para este fluxo'
                : 'Nenhum'}
            </option>
            {availableUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.email})
              </option>
            ))}
          </Form.Select>
          {workflowId !== undefined && (
            <Form.Text className="text-muted">
              Apenas usuários permitidos para este contexto estão disponíveis.
            </Form.Text>
          )}
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Atribuir a Grupo</Form.Label>
          <Form.Select
            value={selectedGroup}
            onChange={(e) => {
              setSelectedGroup(e.target.value);
              setSelectedUser('');
            }}
            disabled={workflowId !== undefined && availableGroups.length === 0}
          >
            <option value="">
              {workflowId !== undefined && availableGroups.length === 0
                ? 'Nenhum grupo disponível para este fluxo'
                : 'Nenhum'}
            </option>
            {availableGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Form.Select>
          {workflowId !== undefined && (
            <Form.Text className="text-muted">
              Apenas grupos permitidos para este contexto estão disponíveis.
            </Form.Text>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleSubmit}>
          Atribuir
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PurchaseRequestDetail;


